// screens/business/BusinessDashboardScreen.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Animated,
  RefreshControl,
  StatusBar,
  Alert,
  Image,
  Modal,
  TextInput,
  FlatList,
  SectionList,
  ActivityIndicator,
  BackHandler,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { useFocusEffect } from '@react-navigation/native';

import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';
import BusinessDashboardService from '../../../services/business/dashboard.service';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';
const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // ✅ Corrected to match backend routes (jobeng-show-all-api.js)
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  teamMembers:    `${API_BASE_URL}/api/data/team_members`,
  activeJobs:     `${API_BASE_URL}/api/data/jobs`,
  applications:   `${API_BASE_URL}/api/data/applications`,
  interviews:     `${API_BASE_URL}/api/data/interviews`,
  offers:         `${API_BASE_URL}/api/data/job_offers`,
  projects:       `${API_BASE_URL}/api/data/projects`,
  clients:        `${API_BASE_URL}/api/data/clients`,
  vendors:        `${API_BASE_URL}/api/data/vendors`,
  contracts:      `${API_BASE_URL}/api/data/contracts`,
  invoices:       `${API_BASE_URL}/api/data/financial_invoices`,
  campaigns:      `${API_BASE_URL}/api/data/campaigns`,
  subscription:   `${API_BASE_URL}/api/business-subscriptions`,
  revenue:        `${API_BASE_URL}/api/financial/revenue`,
  employees:      `${API_BASE_URL}/api/data/employees`,
  recruitmentDashboard: `${API_BASE_URL}/api/recruitment/dashboard`,
  recruitmentFunnel:    `${API_BASE_URL}/api/recruitment/funnel`,
  recruitmentStats:     `${API_BASE_URL}/api/recruitment/stats`,
};

// ==================== AXIOS INSTANCE ====================
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Inject company_id into all requests so backend can filter by company
    const businessId = await getBusinessId();
    if (businessId) {
      config.params = { ...config.params, company_id: parseInt(businessId) };
    }
  } catch (error) {
    console.error('Error in request interceptor:', error);
  }
  return config;
}, (error) => Promise.reject(error));

// Helper to get businessId from storage (try multiple keys)
// Helper to get businessId from storage (try multiple keys)
const getBusinessId = async () => {
  try {
    let id = await storage.getItem('company_id');
    if (!id) id = await storage.getItem('company_id');
    if (!id) {
      const profile = await storage.getObject('company_profile');
      if (profile) {
        id = profile.id ? String(profile.id) : null;
      }
    }
    return id || null;
  } catch (error) {
    console.error('Error getting businessId:', error);
    return null;
  }
};

// Brand palette
const BRAND = {
  blue: '#0e4e96',
  canary: '#fdb913',
  oat: '#f0e0ca',
  sepia: '#edae7b',
  mint: '#19abb5',
  bg: '#f8fafc',
  text: '#0f172a',
  sub: '#475569',
  line: '#e2e8f0',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#8b5cf6',
  white: '#ffffff',
};

export default function BusinessDashboardScreen({ navigation, route }) {
  // ==================== CONTEXT ====================
  const {
    user,
    subscription,
    companyProfile,
    teamMembers,
    businessStats,
    notifications,
    isOnline,
    apiAvailable,
    syncStatus,
    saveSubscription,
    saveTeamMembers,
    updateBusinessStats,
    setNotifications,
    addNotification,
    isFeatureAvailable,
    getSubscriptionStatus,
    syncAllData,
    reloadData,
  } = useBusiness();

  // Get params from navigation if coming from welcome screen
  const { companyProfile: routeCompanyProfile, subscription: routeSubscription, fromWelcome } = route?.params || {};

  // Use route params if available, otherwise use context
  const effectiveCompanyProfile = routeCompanyProfile || companyProfile;
  const effectiveSubscription = routeSubscription || subscription;

  // ==================== LOCAL UI STATE ====================
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('BusinessDashboardScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBusinessSolutions, setShowBusinessSolutions] = useState(false);
  const [showMarketIntelligence, setShowMarketIntelligence] = useState(false);

  // Local company profile state — merges context + route params + live API fetch
  const [localCompanyProfile, setLocalCompanyProfile] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);

  // Resolved company profile: local (live) > route param > context
  const resolvedCompanyProfile = localCompanyProfile || effectiveCompanyProfile;

  // Helper — extract logo from any profile shape
  const extractLogo = (profile) =>
    profile?.logo || profile?.logo_url || profile?.company_logo ||
    profile?.banner_image_url || null;

  // Animation values
  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  // ==================== REFRESH ON FOCUS ====================
  useFocusEffect(
    useCallback(() => {
      refreshData();
    }, [])
  );

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    initializeDashboard();

    const backHandler = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    const refreshInterval = setInterval(refreshBusinessStats, 30000);

    return () => {
      backHandler.remove();
      clearInterval(refreshInterval);
    };
  }, []);

  const handleBackPress = () => {
    if (showNotifications || showQuickActions || showTeamModal || showSearchModal || showBusinessSolutions) {
      setShowNotifications(false);
      setShowQuickActions(false);
      setShowTeamModal(false);
      setShowSearchModal(false);
      setShowBusinessSolutions(false);
      return true;
    }
    return false;
  };

  const initializeDashboard = async () => {
    try {
      setIsLoading(true);
      // Warm-start logo from cache immediately before API responds
      try {
        const cached = await storage.getItem('company_profile');
        if (cached) setLocalCompanyProfile(JSON.parse(cached));
      } catch {}
      await reloadData();

      if (isOnline) {
        await Promise.allSettled([
          fetchCompanyProfile(),
          fetchTeamMembers(),
          fetchBusinessStats(),
          fetchNotifications(),
          fetchSubscription(),
        ]);
      }
    } catch (error) {
      console.error('Error initializing dashboard:', error);
      Alert.alert('Error', 'Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    if (isOnline) {
      await Promise.allSettled([
        fetchCompanyProfile(),      // always sync logo + name on focus
        fetchSubscription(),
        fetchTeamMembers(),
        fetchBusinessStats(),
        fetchNotifications(),
      ]);
    }
  };
 
  // ==================== API FETCH FUNCTIONS ====================
  const fetchSubscription = async () => {
    try {
      const businessId = await getBusinessId();
      if (!businessId) {
        console.warn('No business ID found, skipping subscription fetch');
        return;
      }
      const response = await api.get(API_ENDPOINTS.subscription, {
        params: { company_id: parseInt(businessId)||0, status: 'active' },
      });
      if (response.data?.success && response.data.data) {
        const subData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
        if (subData) {
          // Normalise price field — use price first, price_paid as fallback
          subData.price = parseFloat(subData.price || subData.price_paid || 0);
          subData.plan_name = subData.plan_name || subData.name || subData.plan_id;
          subData.company_id = businessId;
          await saveSubscription(subData);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.debug('No subscription found (expected for new accounts)');
      } else {
        console.warn('Could not fetch subscription from API, using cached data.', error.message);
      }
    }
  };

  // React to context company profile changes immediately — no manual refresh
  useEffect(() => {
    if (companyProfile?.id) {
      setLocalCompanyProfile(companyProfile);
      storage.setItem('company_profile', JSON.stringify(companyProfile)).catch(() => {});
    }
  }, [companyProfile]);

  const fetchCompanyProfile = async () => {
    const businessId = await getBusinessId();
    if (!businessId) return;
    setLogoLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.companyProfile, { params: { company_id: parseInt(businessId)||0 } });
      if (res.data?.success && res.data.data) {
        const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
        if (raw) {
          setLocalCompanyProfile(raw);
          // Write to all storage keys so every screen that reads company_profile is in sync
          await Promise.allSettled([
            storage.setItem('company_profile', JSON.stringify(raw)),
            storage.setItem('company_name', raw.company_name || raw.name || ''),
            storage.setItem('company_logo',
              raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url || ''),
          ]);
        }
      }
    } catch (err) {
      // Warm start: load from cache if API fails
      try {
        const cached = await storage.getItem('company_profile');
        if (cached) setLocalCompanyProfile(JSON.parse(cached));
      } catch {}
      console.warn('fetchCompanyProfile: using cached data.', err?.message);
    } finally {
      setLogoLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const businessId = await getBusinessId();
      if (!businessId) {
        console.warn('No business ID found, skipping team members fetch');
        return;
      }
      const response = await api.get(API_ENDPOINTS.teamMembers, {
        params: { company_id: parseInt(businessId)||0 },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        await saveTeamMembers(response.data.data);
      }
    } catch (error) {
      console.warn('Could not fetch team members from API, using cached data.', error.message);
    }
  };

  const fetchBusinessStats = async () => {
    try {
      const businessId = await getBusinessId();
      if (!businessId) {
        console.warn('No business ID found, skipping stats fetch');
        return;
      }

      const [
        jobsRes,
        appsRes,
        interviewsRes,
        offersRes,
        projectsRes,
        clientsRes,
        vendorsRes,
        contractsRes,
        invoicesRes,
        campaignsRes,
        revenueRes,
      ] = await Promise.allSettled([
        api.get(API_ENDPOINTS.activeJobs,   { params: { company_id: parseInt(businessId)||0, status:'active'    } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.applications,  { params: { company_id: parseInt(businessId)||0                       } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.interviews,    { params: { company_id: parseInt(businessId)||0, scheduled:1          } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.offers,        { params: { company_id: parseInt(businessId)||0, status:'pending'     } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.projects,      { params: { company_id: parseInt(businessId)||0, status:'active'      } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.clients,       { params: { company_id: parseInt(businessId)||0                       } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.vendors,       { params: { company_id: parseInt(businessId)||0                       } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.contracts,     { params: { company_id: parseInt(businessId)||0, status:'active'      } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.invoices,      { params: { company_id: parseInt(businessId)||0                      } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.campaigns,     { params: { company_id: parseInt(businessId)||0, status:'active'      } }).catch(()=>({data:{success:false}})),
        api.get(API_ENDPOINTS.revenue,       { params: { company_id: parseInt(businessId)||0                       } }).catch(()=>({data:{success:false}})),
      ]);

      const stats = {
        activeJobs: jobsRes.status === 'fulfilled' && jobsRes.value.data?.success ? jobsRes.value.data.data?.length || 0 : 0,
        applications: appsRes.status === 'fulfilled' && appsRes.value.data?.success ? appsRes.value.data.data?.length || 0 : 0,
        interviews: interviewsRes.status === 'fulfilled' && interviewsRes.value.data?.success ? interviewsRes.value.data.data?.length || 0 : 0,
        offers: offersRes.status === 'fulfilled' && offersRes.value.data?.success ? offersRes.value.data.data?.length || 0 : 0,
        projects: projectsRes.status === 'fulfilled' && projectsRes.value.data?.success ? projectsRes.value.data.data?.length || 0 : 0,
        teamMembers: teamMembers.length || 0,
        revenue: (() => {
          const invData = invoicesRes.status === 'fulfilled' && invoicesRes.value.data?.success ? invoicesRes.value.data.data || [] : [];
          return invData.reduce((s, i) => s + parseFloat(i.total_amount || i.total || i.amount || 0), 0);
        })(),
        clients: clientsRes.status === 'fulfilled' && clientsRes.value.data?.success ? clientsRes.value.data.data?.length || 0 : 0,
        vendors: vendorsRes.status === 'fulfilled' && vendorsRes.value.data?.success ? vendorsRes.value.data.data?.length || 0 : 0,
        contracts: contractsRes.status === 'fulfilled' && contractsRes.value.data?.success ? contractsRes.value.data.data?.length || 0 : 0,
        invoices: (() => {
          const allInv = invoicesRes.status === 'fulfilled' && invoicesRes.value.data?.success ? invoicesRes.value.data.data || [] : [];
          return allInv.filter(i => ['pending','sent','overdue'].includes((i.status||'').toLowerCase())).length;
        })(),
        campaigns: campaignsRes.status === 'fulfilled' && campaignsRes.value.data?.success ? campaignsRes.value.data.data?.length || 0 : 0,
      };

      await updateBusinessStats(stats);
    } catch (error) {
      console.warn('Error fetching business stats, using cached values.', error.message);
    }
  };

  const fetchNotifications = async () => {
    try {
      const businessId = await getBusinessId();
      if (!businessId) {
        console.warn('No business ID found, skipping notifications fetch');
        return;
      }

      const [applications, interviews, offers] = await Promise.allSettled([
        api.get(API_ENDPOINTS.applications, { params: { company_id: parseInt(businessId)||0, limit: 5, sort: 'created_at', order: 'DESC' } }),
        api.get(API_ENDPOINTS.interviews, { params: { company_id: parseInt(businessId)||0, limit: 5, sort: 'interview_date', order: 'ASC' } }),
        api.get(API_ENDPOINTS.offers, { params: { company_id: parseInt(businessId)||0, limit: 5, sort: 'created_at', order: 'DESC' } }),
      ]);

      const list = [];
      if (applications.status === 'fulfilled' && applications.value.data?.success) {
        applications.value.data.data?.forEach((app, idx) => {
          list.push({
            id: `app-${app.id || idx}`,
            title: 'New Application',
            message: `${app.candidate_name || 'A candidate'} applied for ${app.job_title || 'a position'}`,
            time: timeAgo(new Date(app.created_at)),
            type: 'application',
            priority: 'high',
          });
        });
      }
      if (interviews.status === 'fulfilled' && interviews.value.data?.success) {
        interviews.value.data.data?.forEach((int, idx) => {
          list.push({
            id: `int-${int.id || idx}`,
            title: 'Interview Scheduled',
            message: `Interview with ${int.candidate_name || 'candidate'} on ${new Date(int.interview_date).toLocaleDateString()}`,
            time: timeAgo(new Date(int.created_at)),
            type: 'interview',
            priority: 'medium',
          });
        });
      }
      if (offers.status === 'fulfilled' && offers.value.data?.success) {
        offers.value.data.data?.forEach((off, idx) => {
          list.push({
            id: `off-${off.id || idx}`,
            title: 'Job Offer',
            message: `Offer sent to ${off.candidate_name || 'candidate'} for ${off.job_title || 'position'}`,
            time: timeAgo(new Date(off.created_at)),
            type: 'offer',
            priority: 'medium',
          });
        });
      }

      setNotifications(list.slice(0, 10));
    } catch (error) {
      console.warn('Error fetching notifications, using cached.', error.message);
    }
  };

  const refreshBusinessStats = async () => {
    if (isOnline) {
      await Promise.allSettled([fetchBusinessStats(), fetchNotifications()]);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await reloadData();
    if (isOnline) {
      await Promise.allSettled([
        fetchCompanyProfile(),
        fetchTeamMembers(),
        fetchBusinessStats(),
        fetchNotifications(),
        fetchSubscription(),
      ]);
    }
    setRefreshing(false);
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    const intervals = [
      [31536000, 'years'],
      [2592000, 'months'],
      [86400, 'days'],
      [3600, 'hours'],
      [60, 'minutes'],
    ];
    for (const [sec, unit] of intervals) {
      const interval = seconds / sec;
      if (interval > 1) return `${Math.floor(interval)} ${unit} ago`;
    }
    return `${Math.floor(seconds)} seconds ago`;
  };

  // ==================== HELPER FUNCTIONS ====================
  const getUserInitials = (name) => {
    if (!name || typeof name !== 'string') return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getFirstName = (name) => {
    if (!name || typeof name !== 'string') return 'User';
    return name.split(' ')[0];
  };

  const handleSubscriptionUpgrade = () => {
    if (navigation && navigation.navigate) {
      navigation.navigate('BusinessSubscriptionSystem', {
        currentPlan: effectiveSubscription,
        companyProfile: resolvedCompanyProfile,
        onPlanSelected: (newSubscription) => {
          saveSubscription(newSubscription);
        }
      });
    }
  };

  const handleManageSubscription = () => {
    if (navigation && navigation.navigate && effectiveSubscription) {
      navigation.navigate('BusinessPaymentSystem', {
        subscription: effectiveSubscription,
        companyProfile: resolvedCompanyProfile,
      });
    }
  };

  const handlePaymentCenter = () => {
    if (navigation && navigation.navigate) {
      navigation.navigate('BusinessPaymentSystem', {
        subscription: effectiveSubscription,
        companyProfile: resolvedCompanyProfile,
      });
    }
  };

  const getQuickActions = () => [
    { title: 'Active Jobs', icon: 'briefcase-check', color: '#10B981', screen: 'ActiveJobsScreen', available: isFeatureAvailable('starter') },
    { title: 'Post Job', icon: 'briefcase-plus', color: '#2196F3', screen: 'PostJobScreen', available: isFeatureAvailable('starter') },
    { title: 'View Applicants', icon: 'account-multiple', color: '#4CAF50', screen: 'ApplicantsPipelineScreen', available: isFeatureAvailable('starter') },
    { title: 'Schedule Interview', icon: 'calendar-plus', color: '#FF9800', screen: 'InterviewManagementScreen', available: isFeatureAvailable('starter') },
    { title: 'Team Management', icon: 'account-group', color: '#607D8B', screen: 'TeamManagementScreen', available: isFeatureAvailable('team') },
    { title: 'Financial Reports', icon: 'chart-line', color: '#795548', screen: 'FinancialManagement', available: isFeatureAvailable('professional') },
    { title: 'Payment Center', icon: 'credit-card', color: BRAND.blue, screen: 'BusinessPaymentSystem', available: true },
    { title: 'Scale Up Business', icon: 'rocket', color: BRAND.blue, screen: 'ScaleUpBusinessScreen', available: isFeatureAvailable('enterprise') },
    { title: 'Email Templates', icon: 'email-edit', color: BRAND.mint, screen: 'EmailTemplatesScreen', available: isFeatureAvailable('professional') },
    { title: 'Contracts', icon: 'file-document', color: BRAND.blue, screen: 'ContractsScreen', available: isFeatureAvailable('professional') },
    { title: 'Annual Audit', icon: 'file-search', color: '#EF4444', screen: 'AnnualAuditScreen', available: isFeatureAvailable('professional') },
    { title: 'Annual Revenue', icon: 'chart-bar', color: '#10B981', screen: 'AnnualRevenueScreen', available: isFeatureAvailable('professional') },
    { title: 'Market Insights', icon: 'trending-up', color: '#1e7be2', screen: 'MarketInsights', available: true },
  ];

  const navigateToScreen = (screenName, params = {}, requiredPlan = null) => {
    if (navigation && navigation.navigate) {
      if (requiredPlan && !isFeatureAvailable(requiredPlan)) {
        Alert.alert(
          'Subscription Required',
          `This feature requires ${requiredPlan} plan. Upgrade to access.`,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
          ]
        );
        return;
      }
      navigation.navigate(screenName, {
        ...params,
        companyProfile: resolvedCompanyProfile,
        companyLogo: resolvedCompanyProfile?.logo || null,
        companyName: resolvedCompanyProfile?.name || resolvedCompanyProfile?.company_name || '',
        subscription: effectiveSubscription,
      });
    } else {
      Alert.alert('Navigation Error', `Cannot navigate to ${screenName}. Navigation not available.`);
    }
  };

  // All business solutions (original array – kept intact)
  const allBusinessSolutions = [
    // RECRUITMENT & TALENT MANAGEMENT
    {
      category: 'Recruitment & Talent Management',
      icon: 'account-search',
      color: '#4CAF50',
      requiredPlan: 'starter',
      solutions: [
        { title: 'Recruitment Flow', icon: 'account-switch', screen: 'RecruitmentFlow', description: 'Recruitment workflow', requiredPlan: 'starter' },
        { title: 'Active Jobs', icon: 'briefcase-check', screen: 'ActiveJobsScreen', description: 'View active job postings', requiredPlan: 'starter' },
        { title: 'Post Job', icon: 'briefcase-plus', screen: 'PostJobScreen', description: 'Create and publish job listings', requiredPlan: 'starter' },
        
        { title: 'Applicants', icon: 'account-multiple', screen: 'ApplicantsPoolScreen', description: 'View and manage job applications', requiredPlan: 'starter' },
        { title: 'Applicant Selection', icon: 'account-check', screen: 'ApplicantSelectionScreen', description: 'Select qualified candidates', requiredPlan: 'starter' },
        { title: 'Qualified Candidates', icon: 'account-star', screen: 'QualifiedApplicantsScreen', description: 'Track top performers', requiredPlan: 'professional' },
      
        { title: 'In-Person Interview', icon: 'account-tie', screen: 'InPersonInterviewScreen', description: 'Manage in-person interviews', requiredPlan: 'starter' },
       
       
        { title: 'Tests & Assessments', icon: 'school', screen: 'TestApplicantsScreen', description: 'Test candidate skills', requiredPlan: 'professional' },
       
       
        { title: 'Job Offer Management', icon: 'handshake', screen: 'JobOfferManagementScreen', description: 'Manage job offers', requiredPlan: 'starter' },
        
        { title: 'Accepted Offers', icon: 'check-circle', screen: 'AcceptedOffersScreen', description: 'Track accepted offers', requiredPlan: 'starter' },
        { title: 'Hired Pipeline', icon: 'account-check', screen: 'HiredScreen', description: 'Manage hired employees', requiredPlan: 'starter' },
        { title: 'Applicant Pipeline', icon: 'filter-variant', screen: 'ApplicantsPipelineScreen', description: 'Full recruitment pipeline', requiredPlan: 'starter' },
        
        
        { title: 'Internship Management', icon: 'school', screen: 'InternshipManagementScreen', description: 'Manage internships', requiredPlan: 'professional' },
        { title: 'Smart Contract templates', icon: 'file-cog', screen: 'SmartContractTemplates', description: 'Smart Contract', requiredPlan: 'enterprise' },
        
        
        { title: 'Mentor Dashboard', icon: 'file-cog', screen: 'MentorDashboardScreen', description: 'Mentor', requiredPlan: 'professional' },
        { title: 'Networking', icon: 'account-network', screen: 'NetworkingScreen', description: 'Networking', requiredPlan: 'professional' },
      ],
    },
    // PROJECTS & MANAGEMENT
    {
      category: 'Project & Team Management',
      icon: 'clipboard-list',
      color: '#2196F3',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Manage Projects', icon: 'clipboard-check', screen: 'ManageProjects', description: 'Oversee all projects', requiredPlan: 'professional' },
        
        { title: 'Project Assignment', icon: 'account-arrow-right', screen: 'ProjectAssignment', description: 'Assign team members', requiredPlan: 'professional' },
        { title: 'Project Notifications', icon: 'bell', screen: 'NotificationsProjectScreen', description: 'Project alerts', requiredPlan: 'professional' },
        
        { title: 'Project Schedule', icon: 'calendar-clock', screen: 'ProjectPlanningScreen', description: 'Project Planning', requiredPlan: 'professional' },
      ],
    },
    // TEAM MANAGEMENT
    {
      category: 'Team Management',
      icon: 'clipboard-list',
      color: '#2196F3',
      requiredPlan: 'team',
      solutions: [
        { title: 'Team Management', icon: 'account-group', screen: 'TeamManagementScreen', description: 'Manage teams', requiredPlan: 'team' },
        { title: 'Employee Management', icon: 'account-multiple', screen: 'EmployeeManagementScreen', description: 'Manage employee', requiredPlan: 'team' },
        { title: 'Payroll Management', icon: 'account-cash', screen: 'PayrollManagementScreen', description: 'Payroll', requiredPlan: 'team' },
        { title: 'HR Dashboard', icon: 'clipboard-account', screen: 'HRDashboardScreen', description: 'Human resources', requiredPlan: 'team' },
      ],
    },
    // VENDOR & SUPPLY CHAIN MANAGEMENT
    {
      category: 'Vendor & Supply Chain Management',
      icon: 'truck',
      color: '#FF9800',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Suppliers List', icon: 'truck', screen: 'SuppliersVendorList', description: 'Manage suppliers', requiredPlan: 'professional' },
        { title: 'Add Supplier', icon: 'plus-circle', screen: 'AddSupplierScreen', description: 'Add new suppliers', requiredPlan: 'professional' },
        { title: 'Edit Supplier', icon: 'pencil', screen: 'EditSupplierScreen', description: 'Edit supplier details', requiredPlan: 'professional' },
        { title: 'Subcontractors', icon: 'account-hard-hat', screen: 'SubcontractorsVendorList', description: 'Manage subcontractors', requiredPlan: 'professional' },
        { title: 'Subcontractor Details', icon: 'information', screen: 'SubcontractorDetail', description: 'View subcontractor info', requiredPlan: 'professional' },
        { title: 'Add/Edit Subcontractor', icon: 'account-plus', screen: 'AddEditSubcontractor', description: 'Manage subcontractors', requiredPlan: 'professional' },
        { title: 'Vendor Categories', icon: 'tag', screen: 'VendorCategories', description: 'Categorize vendors', requiredPlan: 'professional' },
        { title: 'Vendor Performance Dashboard', icon: 'chart-bar', screen: 'VendorPerformanceDashboard', description: 'Track performance', requiredPlan: 'enterprise' },
      ],
    },
    // FINANCIAL MANAGEMENT
    {
      category: 'Financial Management',
      icon: 'calculator',
      color: '#4CAF50',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Financial Overview', icon: 'chart-line', screen: 'FinancialManagement', description: 'Financial dashboard', requiredPlan: 'professional' },
        { title: 'Invoice Management', icon: 'file-document', screen: 'InvoicesManagement', description: 'Manage invoices', requiredPlan: 'professional' },
        { title: 'Create Invoice', icon: 'file-plus', screen: 'CreateInvoiceScreen', description: 'Create new invoices', requiredPlan: 'professional' },
       
        { title: 'Tenders Management', icon: 'gavel', screen: 'TendersManagement', description: 'Manage tenders', requiredPlan: 'professional' },
       
        { title: 'Smart Contract Payment', icon: 'credit-card', screen: 'SmartContractPaymentScreen', description: 'Payment processing', requiredPlan: 'enterprise' },
        { title: 'Budget Settings', icon: 'currency-usd', screen: 'BudgetSettings', description: 'Budget management', requiredPlan: 'professional' },
        { title: 'Financial Analytics', icon: 'chart-pie', screen: 'FinancialAnalyticsDashboard', description: 'Financial insights', requiredPlan: 'enterprise' },
        { title: 'Expense Tracking', icon: 'receipt', screen: 'ExpenseTrackingScreen', description: 'Track expenses', requiredPlan: 'professional' },
        { title: 'Revenue Tracking', icon: 'cash-multiple', screen: 'RevenueTrackingScreen', description: 'Track revenue', requiredPlan: 'professional' },
        { title: 'Contracts', icon: 'file-document', color: BRAND.blue, screen: 'ContractsScreen', available: isFeatureAvailable('professional') },
        { title: 'Annual Audit', icon: 'file-search', screen: 'AnnualAuditScreen', description: 'Conduct annual audits', requiredPlan: 'enterprise' },
        { title: 'Annual Revenue', icon: 'chart-bar', screen: 'AnnualRevenueScreen', description: 'View annual revenue', requiredPlan: 'enterprise' },

      ],
    },
    // MARKETING & CAMPAIGNS
    {
      category: 'Marketing & Campaigns',
      icon: 'bullhorn',
      color: '#E91E63',
      requiredPlan: 'enterprise',
      solutions: [
        { title: 'Marketing & Promotion', icon: 'bullhorn', screen: 'MarketingPromotionScreen', description: 'Marketing tools', requiredPlan: 'enterprise' },
        { title: 'Campaign List', icon: 'bullhorn', screen: 'CampaignList', description: 'View campaigns', requiredPlan: 'enterprise' },
        
       
        { title: 'Add Campaign Asset', icon: 'image-plus', screen: 'AddCampaignAssetScreen', description: 'Add assets', requiredPlan: 'enterprise' },
        
        
        
        { title: 'AI Content Assistant', icon: 'robot', screen: 'CampaignAIContentAssistantScreen', description: 'AI content help', requiredPlan: 'enterprise' },
      ],
    },
    // COMPLIANCE & REGULATORY
    {
      category: 'Compliance & Regulatory',
      icon: 'shield-check',
      color: '#9C27B0',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Compliance & Regulatory', icon: 'shield-check', screen: 'ComplianceRegulatoryScreen', description: 'Compliance tools', requiredPlan: 'professional' },
        { title: 'Compliance Check', icon: 'check-circle', screen: 'ComplianceCheckScreen', description: 'Run compliance checks', requiredPlan: 'professional' },
        { title: 'Compliance Details', icon: 'information', screen: 'ComplianceDetailsScreen', description: 'Compliance info', requiredPlan: 'professional' },
        { title: 'Compliance Report Generator', icon: 'file-chart', screen: 'ComplianceReportGeneratorScreen', description: 'Generate reports', requiredPlan: 'professional' },
        { title: 'Compliance Deadline Details', icon: 'calendar-alert', screen: 'ComplianceDeadlineDetailsScreen', description: 'Deadline info', requiredPlan: 'professional' },
        { title: 'Regulatory Update Details', icon: 'update', screen: 'RegulatoryUpdateDetailsScreen', description: 'Update details', requiredPlan: 'professional' },
      ],
    },
    // COMMUNICATION & COLLABORATION
    {
      category: 'Communication & Collaboration',
      icon: 'message-text',
      color: '#607D8B',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Business Video Conference', icon: 'video', screen: 'BusinessVideoConferenceScreen', description: 'Video meetings', requiredPlan: 'professional' },
        { title: 'Email Templates', icon: 'email-edit', screen: 'EmailTemplatesScreen', description: 'Email templates', requiredPlan: 'professional' },
        { title: 'Schedule Settings', icon: 'calendar-edit', screen: 'ScheduleSettingsScreen', description: 'Schedule management', requiredPlan: 'professional' },
        { title: 'Alert Preferences', icon: 'bell-alert', screen: 'AlertPreferencesScreen', description: 'Alert settings', requiredPlan: 'professional' },
        { title: 'Contact Professional', icon: 'account-network', screen: 'BusinessContactProfessionalScreen', description: 'Professional network', requiredPlan: 'professional' },
      ],
    },
    // ANALYTICS & REPORTING
    {
      category: 'Analytics & Reporting',
      icon: 'chart-box',
      color: '#795548',
      requiredPlan: 'enterprise',
      solutions: [
        { title: 'Analytics Dashboard', icon: 'chart-box', screen: 'CompanyAnalytics', description: 'Business analytics', requiredPlan: 'enterprise' },
        { title: 'Company Info', icon: 'office-building', screen: 'BusinessCompanyInfoScreen', description: 'Company information', requiredPlan: 'starter' },
        { title: 'Ratings & Reviews', icon: 'star', screen: 'RatingsReviewsScreen', description: 'Customer feedback', requiredPlan: 'professional' },
        { title: 'Business Intelligence', icon: 'brain', screen: 'BusinessIntelligenceScreen', description: 'Advanced analytics', requiredPlan: 'enterprise' },
      ],
    },
    // CLIENTS
    {
      category: 'Client & Customer Management',
      icon: 'briefcase-account',
      color: '#485d79ff',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Clients', icon: 'briefcase-account', screen: 'ClientsScreen', description: 'Clients', requiredPlan: 'professional' },
        { title: 'Ratings & Reviews', icon: 'star', screen: 'RatingsReviewsScreen', description: 'Customer feedback', requiredPlan: 'professional' },
      ],
    },
    // CERTIFICATION & TRAINING
    {
      category: 'Certification & Training',
      icon: 'certificate',
      color: '#FF5722',
      requiredPlan: 'professional',
      solutions: [
        { title: 'Certifications', icon: 'certificate', screen: 'CertificationsScreen', description: 'View certifications', requiredPlan: 'professional' },
       
      ],
    },
    // COMPANY MANAGEMENT (large list)
    {
      category: 'Company Management',
      icon: 'office-building',
      color: '#3F51B5',
      requiredPlan: 'starter',
      solutions: [
        
        { title: 'Product', icon: 'package-variant-closed', screen: 'ProjectProductCreationScreen', description: 'Product', requiredPlan: 'professional' },
        
        { title: 'Website Managment', icon: 'cog-outline', screen: 'WebsiteManagementScreen', description: 'Website', requiredPlan: 'professional' },
        
        { title: 'Company Events', icon: 'calendar', screen: 'CompanyEventsScreen', description: 'Company events', requiredPlan: 'professional' },
        { title: 'Invoice System', icon: 'hand-wave', screen: 'InvoiceSystemScreen', description: 'Invoices System', requiredPlan: 'starter' },
        
        { title: 'Company Management', icon: 'office-building', screen: 'CompanyManagementScreen', description: 'Company Management', requiredPlan: 'starter' },
        { title: 'Company Info', icon: 'office-building', screen: 'BusinessCompanyInfoScreen', description: 'Company information', requiredPlan: 'starter' },
        
               
        { title: 'Logo design', icon: 'calendar-edit', screen: 'LogoDesignScreen', description: 'Logo design', requiredPlan: 'professional' },
        
        
        { title: 'Certifications', icon: 'certificate', screen: 'Certifications', description: 'View certifications', requiredPlan: 'professional' },
        
        { title: 'Team Management', icon: 'account-group', screen: 'TeamManagementScreen', description: 'Manage teams', requiredPlan: 'team' },
        { title: 'Employee Management', icon: 'account-multiple', screen: 'EmployeeManagementScreen', description: 'Manage employee', requiredPlan: 'team' },
        { title: 'Payroll Management', icon: 'account-cash', screen: 'PayrollManagementScreen', description: 'Payroll', requiredPlan: 'team' },
        { title: 'HR Dashboard', icon: 'clipboard-account', screen: 'HRDashboardScreen', description: 'Human resources', requiredPlan: 'team' },
        { title: 'Sales', icon: 'package-variant-closed', screen: 'CompanySalesDepartmentScreen', description: 'Sales', requiredPlan: 'professional' },
        { title: 'Purchase', icon: 'clipboard-text', screen: 'CompanyPurchaseDepartmentScreen', description: 'Purchase Orders', requiredPlan: 'professional' },
        { title: 'Admin access', icon: 'map-marker-check', screen: 'AdminHRManagementScreen', description: 'Admin access', requiredPlan: 'professional' },
        { title: 'Company Analytics', icon: 'calendar-remove', screen: 'CompanyAnalytics', description: 'Company Analytics', requiredPlan: 'team' },
        { title: 'Admin HR Management', icon: 'account-cog', screen: 'AdminHRManagementScreen', description: 'Admin', requiredPlan: 'enterprise' },
        
        { title: 'Tenders ,Bidding and Quotation', icon: 'file-document-edit', screen: 'TenderScreen', description: 'Tenders ,Bidding and Quotation', requiredPlan: 'professional' },
        
        { title: 'Email Management', icon: 'email', screen: 'EmailManagementScreen', description: 'Email Management', requiredPlan: 'professional' },
        { title: 'Letters Management', icon: 'email-edit', screen: 'LettersManagementScreen', description: 'Letters Management', requiredPlan: 'professional' },
        { title: 'Environmental Product Responsibility', icon: 'file-tree-outline', screen: 'EPRScreen', description: 'EPR Management', requiredPlan: 'enterprise' },
         { title: 'CRMS', icon: 'email-edit', screen: 'CRMScreen', description: 'Customer Relationship Management', requiredPlan: 'professional' },
        { title: 'Machine Learning', icon: 'file-document', screen: 'MachineLearningScreen', description: 'Files Managment', requiredPlan: 'enterprise' },
        { title: 'Enterprise Resource Planning (ERP)', icon: 'office-building-cog-outline', screen: 'ERPSystemScreen', description: 'ERP System', requiredPlan: 'enterprise' },
       { title: 'Documents Control', icon: 'file-lock-outline', screen: 'DocumentsControlScreen', description: 'Documents Control', requiredPlan: 'enterprise' },
        { title: 'Scale Up Business', icon: 'file-document', screen: 'ScaleUpBusinessScreen', description: 'Scale up your business', requiredPlan: 'enterprise' },
        { title: 'Innovative Department', icon: 'office-building-cog-outline', screen: 'InnovativeScreen', description: 'Innovations', requiredPlan: 'enterprise' },
         { title: 'AI Agent', icon: 'robot-outline', screen: 'AIAgentScreen', description: 'AI Agent', requiredPlan: 'enterprise' },
      ],
    },
  ];

  // Activity icon/color helpers
  const getActivityIcon = (type) => {
    switch (type) {
      case 'application': return 'account-plus';
      case 'interview': return 'calendar-clock';
      case 'offer': return 'email-check';
      default: return 'information';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'application': return '#4CAF50';
      case 'interview': return '#FF9800';
      case 'offer': return '#2196F3';
      default: return '#64748B';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#E91E63';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#94A3B8';
    }
  };

  // ==================== RENDER FUNCTIONS ====================
  const renderHeader = () => {
    const subscriptionStatus = getSubscriptionStatus();
    return (
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.logoContainer}
            onPress={() => navigateToScreen('CompanyProfileScreen', { companyProfile: resolvedCompanyProfile })}
          >
            {logoLoading && !extractLogo(resolvedCompanyProfile) ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : extractLogo(resolvedCompanyProfile) ? (
              <Image
                source={{ uri: extractLogo(resolvedCompanyProfile) || undefined }}
                style={styles.logoImage}
                resizeMode="cover"
                onError={() => {/* keep showing initials on broken URL */}}
              />
            ) : (
              <View style={styles.logoInitialsWrap}>
                <Text style={styles.logoInitialsText}>
                  {(resolvedCompanyProfile?.company_name || resolvedCompanyProfile?.name || user?.company_name || 'JE')
                    .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'JE'}
                </Text>
              </View>
            )}
            {/* Live sync dot */}
            {logoLoading && (
              <View style={styles.logoDot} />
            )}
          </TouchableOpacity>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {resolvedCompanyProfile?.company_name || resolvedCompanyProfile?.name ||
               user?.company_name || 'JOBENG Construction Ltd'}
            </Text>
            <Text style={styles.companyType}>
              {resolvedCompanyProfile?.industry || effectiveCompanyProfile?.industry || 'Construction & Engineering'}
            </Text>
            <View style={styles.subscriptionStatusHeader}>
              <Ionicons name={subscriptionStatus.icon} size={12} color={subscriptionStatus.color} />
              <Text style={[styles.subscriptionStatusText, { color: subscriptionStatus.color }]}>{subscriptionStatus.message}</Text>
              {effectiveSubscription && (
                <TouchableOpacity style={styles.manageSubscriptionButton} onPress={handleManageSubscription}>
                  <Text style={styles.manageSubscriptionText}>Manage</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.companyStats}>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>{teamMembers.length || businessStats.teamMembers}</Text>
                <Text style={styles.statLabel}>Employees</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>
                  {resolvedCompanyProfile?.founded || effectiveCompanyProfile?.founded || 2015}
                </Text>
                <Text style={styles.statLabel}>Established</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statNumber}>{businessStats.projects}</Text>
                <Text style={styles.statLabel}>Projects</Text>
              </View>
            </View>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity style={styles.headerButton} onPress={() => setShowSearchModal(true)}>
              <Ionicons name="search" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerButton} onPress={() => setShowNotifications(true)}>
              <Ionicons name="notifications" size={20} color="#fff" />
              {notifications.length > 0 && (
                <View style={styles.notificationBadge}>
                  <Text style={styles.notificationBadgeText}>{notifications.length}</Text>
                </View>
              )}
            </TouchableOpacity>
            <TouchableOpacity style={styles.profileButton} onPress={() => navigateToScreen('BusinessUserProfileScreen')}>
              <View style={styles.profileAvatar}>
                <Text style={styles.profileInitials}>{getUserInitials(user?.full_name || 'User')}</Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.metricsContainer}>
          <View style={styles.metricCard}>
            <Text style={styles.metricValue}>{businessStats.revenue > 0 ? `£${(businessStats.revenue / 1000).toFixed(1)}K` : '£0K'}</Text>
            <Text style={styles.metricLabel}>Annual Revenue</Text>
            <View style={styles.metricChange}>
              <Ionicons name="trending-up" size={12} color="#10b981" />
              <Text style={styles.metricChangeText}>+12.5%</Text>
            </View>
          </View>
          <View style={styles.metricCard}>
            <Text style={styles.metricValue}>{businessStats.clients}</Text>
            <Text style={styles.metricLabel}>Active Clients</Text>
            <View style={styles.metricChange}>
              <Ionicons name="trending-up" size={12} color="#10b981" />
              <Text style={styles.metricChangeText}>+8%</Text>
            </View>
          </View>
        </View>
        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.actionButton} onPress={() => navigateToScreen('CompanyProfileScreen', { companyProfile: resolvedCompanyProfile })}>
            <MaterialCommunityIcons name="account-edit" size={16} color="#fff" />
            <Text style={styles.actionButtonText}>Edit Profile</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionButton} onPress={() => navigateToScreen('CompanyEventsScreen')}>
            <MaterialCommunityIcons name="calendar" size={16} color="#fff" />
            <Text style={styles.actionButtonText}>Events</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionButton} onPress={() => setShowTeamModal(true)}>
            <MaterialCommunityIcons name="account-group" size={16} color="#fff" />
            <Text style={styles.actionButtonText}>Team</Text>
          </TouchableOpacity>
          {!effectiveSubscription && (
            <TouchableOpacity style={[styles.actionButton, { backgroundColor: 'rgba(255,255,255,0.3)' }]} onPress={handleSubscriptionUpgrade}>
              <MaterialCommunityIcons name="crown" size={16} color="#FFD700" />
              <Text style={styles.actionButtonText}>Upgrade</Text>
            </TouchableOpacity>
          )}
          {effectiveSubscription && (
            <TouchableOpacity style={[styles.actionButton, { backgroundColor: 'rgba(255,255,255,0.3)' }]} onPress={handlePaymentCenter}>
              <MaterialCommunityIcons name="credit-card" size={16} color="#fff" />
              <Text style={styles.actionButtonText}>Payment</Text>
            </TouchableOpacity>
          )}
        </View>
        {syncStatus === 'syncing' && (
          <View style={styles.syncIndicator}>
            <ActivityIndicator size="small" color="#fff" />
            <Text style={styles.syncText}>Syncing...</Text>
          </View>
        )}
        {!isOnline && (
          <View style={styles.offlineIndicator}>
            <Ionicons name="cloud-offline" size={16} color="#fff" />
            <Text style={styles.offlineText}>Offline Mode</Text>
          </View>
        )}
      </LinearGradient>
    );
  };

  const renderTabNavigation = () => {
    const tabs = [
      { id: 'overview', title: 'Overview', icon: 'view-dashboard' },
      { id: 'recruitment', title: 'Recruitment', icon: 'account-search' },
      { id: 'projects', title: 'Projects', icon: 'clipboard-list' },
      { id: 'vendor', title: 'Vendor', icon: 'truck' },
      { id: 'financial', title: 'Financial', icon: 'calculator' },
      { id: 'marketing', title: 'Marketing', icon: 'bullhorn' },
      { id: 'compliance', title: 'Compliance', icon: 'shield-check' },
      { id: 'analytics', title: 'Analytics', icon: 'chart-box' },
      { id: 'clients', title: 'Clients', icon: 'briefcase-account' },
      { id: 'company management', title: 'Company Management', icon: 'office-building' },
      { id: 'insights', title: '📊 Market Insights', icon: 'trending-up' },
    ];
    return (
      <View style={styles.tabNavigation}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {tabs.map(tab => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tab, activeTab === tab.id && styles.activeTab]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons name={tab.icon} size={18} color={activeTab === tab.id ? '#4A90E2' : '#64748B'} />
              <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>{tab.title}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  // Render Overview Tab
  const renderOverviewTab = () => {
    const quickActions = getQuickActions();
    const subscriptionStatus = getSubscriptionStatus();

    return (
      <View style={styles.tabContent}>
        <View style={styles.welcomeSection}>
          <Text style={styles.welcomeTitle}>Welcome back, {getFirstName(user?.full_name || 'User')}!</Text>
          <Text style={styles.welcomeSubtitle}>Here's what's happening with your business today</Text>
          <View style={styles.subscriptionBadge}>
            <Ionicons name={subscriptionStatus.icon} size={16} color={subscriptionStatus.color} />
            <Text style={[styles.subscriptionText, { color: subscriptionStatus.color }]}>{subscriptionStatus.message}</Text>
            {!effectiveSubscription && (
              <TouchableOpacity style={styles.upgradeInlineButton} onPress={handleSubscriptionUpgrade}>
                <Text style={styles.upgradeInlineButtonText}>Upgrade</Text>
              </TouchableOpacity>
            )}
            {effectiveSubscription && (
              <TouchableOpacity style={styles.manageInlineButton} onPress={handleManageSubscription}>
                <Text style={styles.manageInlineButtonText}>Manage</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.statsGrid}>
          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('PostJobScreen', {}, 'starter')}>
            <View style={[styles.statIcon, { backgroundColor: '#E3F2FD' }]}>
              <MaterialCommunityIcons name="briefcase-plus" size={24} color="#2196F3" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.activeJobs}</Text>
            <Text style={styles.statCardLabel}>Active Jobs</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('PostJobScreen', {}, 'starter')}>
              <Text style={styles.statCardActionText}>Post New Job</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('ApplicantsPoolScreen', {}, 'starter')}>
            <View style={[styles.statIcon, { backgroundColor: '#E8F5E8' }]}>
              <MaterialCommunityIcons name="account-multiple" size={24} color="#4CAF50" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.applications}</Text>
            <Text style={styles.statCardLabel}>Applications</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('ApplicantsPoolScreen', {}, 'starter')}>
              <Text style={styles.statCardActionText}>View All</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('InterviewManagementScreen', {}, 'starter')}>
            <View style={[styles.statIcon, { backgroundColor: '#FFF3E0' }]}>
              <MaterialCommunityIcons name="calendar-clock" size={24} color="#FF9800" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.interviews}</Text>
            <Text style={styles.statCardLabel}>Interviews</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('InterviewManagementScreen', {}, 'starter')}>
              <Text style={styles.statCardActionText}>Schedule</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('JobOfferManagementScreen', {}, 'starter')}>
            <View style={[styles.statIcon, { backgroundColor: '#FCE4EC' }]}>
              <MaterialCommunityIcons name="handshake" size={24} color="#E91E63" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.offers}</Text>
            <Text style={styles.statCardLabel}>Job Offers</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('JobOfferManagementScreen', {}, 'starter')}>
              <Text style={styles.statCardActionText}>Send Offer</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('ManageProjects', {}, 'professional')}>
            <View style={[styles.statIcon, { backgroundColor: '#F3E5F5' }]}>
              <MaterialCommunityIcons name="clipboard-list" size={24} color="#9C27B0" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.projects}</Text>
            <Text style={styles.statCardLabel}>Projects</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('ManageProjects', {}, 'professional')}>
              <Text style={styles.statCardActionText}>Manage</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('SuppliersVendorList', {}, 'professional')}>
            <View style={[styles.statIcon, { backgroundColor: '#FFF8E1' }]}>
              <MaterialCommunityIcons name="truck" size={24} color="#FFC107" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.vendors}</Text>
            <Text style={styles.statCardLabel}>Vendors</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('SuppliersVendorList', {}, 'professional')}>
              <Text style={styles.statCardActionText}>View All</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('InvoicesManagement', {}, 'professional')}>
            <View style={[styles.statIcon, { backgroundColor: '#E8F5E8' }]}>
              <MaterialCommunityIcons name="file-document" size={24} color="#4CAF50" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.invoices}</Text>
            <Text style={styles.statCardLabel}>Invoices</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('CreateInvoiceScreen', {}, 'professional')}>
              <Text style={styles.statCardActionText}>Create</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity style={styles.statCard} onPress={() => navigateToScreen('CampaignList', {}, 'enterprise')}>
            <View style={[styles.statIcon, { backgroundColor: '#FCE4EC' }]}>
              <MaterialCommunityIcons name="bullhorn" size={24} color="#E91E63" />
            </View>
            <Text style={styles.statCardNumber}>{businessStats.campaigns}</Text>
            <Text style={styles.statCardLabel}>Campaigns</Text>
            <TouchableOpacity style={styles.statCardAction} onPress={() => navigateToScreen('CreateCampaign', {}, 'enterprise')}>
              <Text style={styles.statCardActionText}>Create</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </View>

        <View style={styles.quickActionsSection}>
          <View style={styles.sectionHeader}>
            {/* Financial Reports Quick Access */}
        <Text style={styles.sectionTitle}>Financial Reports</Text>
        <View style={{flexDirection:'row',gap:10,marginBottom:16,paddingHorizontal:4}}>
          <TouchableOpacity style={{flex:1,backgroundColor:'#0e4e96',borderRadius:14,padding:14}} activeOpacity={0.85}
            onPress={() => navigateToScreen('AnnualAuditScreen')}>
            <MaterialCommunityIcons name="shield-check" size={22} color="#fff" />
            <Text style={{fontSize:14,fontWeight:'800',color:'#fff',marginTop:6}}>Annual Audit</Text>
            <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:2}}>Full audit with PDF</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{flex:1,backgroundColor:'#10B981',borderRadius:14,padding:14}} activeOpacity={0.85}
            onPress={() => navigateToScreen('AnnualRevenueScreen')}>
            <MaterialCommunityIcons name="chart-line" size={22} color="#fff" />
            <Text style={{fontSize:14,fontWeight:'800',color:'#fff',marginTop:6}}>Revenue</Text>
            <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:2}}>MRR, ARR, forecast</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{flex:1,backgroundColor:'#8B5CF6',borderRadius:14,padding:14}} activeOpacity={0.85}
            onPress={() => navigateToScreen('FinancialManagement')}>
            <MaterialCommunityIcons name="calculator" size={22} color="#fff" />
            <Text style={{fontSize:14,fontWeight:'800',color:'#fff',marginTop:6}}>Finance</Text>
            <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:2}}>Invoices, tax, budgets</Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.sectionTitle}>Quick Actions</Text>
            {!effectiveSubscription && (
              <TouchableOpacity style={styles.upgradeSectionButton} onPress={handleSubscriptionUpgrade}>
                <MaterialCommunityIcons name="crown" size={16} color="#FFD700" />
                <Text style={styles.upgradeSectionButtonText}>Upgrade Plan</Text>
              </TouchableOpacity>
            )}
          </View>
          <View style={styles.quickActionsGrid}>
            {quickActions.map((action, index) => (
              <TouchableOpacity
                key={action.title}
                style={[styles.quickActionCard, !action.available && styles.lockedActionCard]}
                onPress={() => {
                  if (action.available) {
                    navigateToScreen(action.screen);
                  } else {
                    Alert.alert(
                      'Subscription Required',
                      'This feature requires a subscription. Would you like to upgrade?',
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  }
                }}
              >
                <View style={[styles.quickActionIcon, { backgroundColor: `${action.color}20` }]}>
                  <MaterialCommunityIcons name={action.icon} size={20} color={action.available ? action.color : '#94A3B8'} />
                  {!action.available && (
                    <View style={styles.lockOverlay}>
                      <Ionicons name="lock-closed" size={12} color="#fff" />
                    </View>
                  )}
                </View>
                <Text style={[styles.quickActionText, !action.available && styles.lockedActionText]}>{action.title}</Text>
                {!action.available && (
                  <View style={styles.quickActionLockBadge}>
                    <Text style={styles.quickActionLockText}>LOCKED</Text>
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {!effectiveSubscription && (
          <View style={styles.subscriptionBenefitsSection}>
            <View style={styles.benefitsHeader}>
              <MaterialCommunityIcons name="crown" size={24} color="#FFD700" />
              <Text style={styles.benefitsTitle}>Upgrade to Unlock All Features</Text>
            </View>
            <View style={styles.benefitsGrid}>
              <View style={styles.benefitItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#4CAF50" />
                <Text style={styles.benefitText}>Unlimited Job Postings</Text>
              </View>
              <View style={styles.benefitItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#4CAF50" />
                <Text style={styles.benefitText}>Advanced Analytics</Text>
              </View>
              <View style={styles.benefitItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#4CAF50" />
                <Text style={styles.benefitText}>Team Collaboration</Text>
              </View>
              <View style={styles.benefitItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#4CAF50" />
                <Text style={styles.benefitText}>Priority Support</Text>
              </View>
            </View>
            <TouchableOpacity style={styles.upgradeCtaButton} onPress={handleSubscriptionUpgrade}>
              <MaterialCommunityIcons name="crown" size={20} color="#fff" />
              <Text style={styles.upgradeCtaButtonText}>View Subscription Plans</Text>
            </TouchableOpacity>
          </View>
        )}


        {/* ── Market Intelligence Card ────────────────────────────── */}
        <View style={styles.marketCard}>
          <TouchableOpacity
            style={styles.marketCardHeader}
            onPress={() => setShowMarketIntelligence(!showMarketIntelligence)}
            activeOpacity={0.85}
          >
            <View style={styles.marketCardLeft}>
              <View style={styles.marketCardIcon}>
                <MaterialCommunityIcons name="trending-up" size={20} color="#1e7be2" />
              </View>
              <View>
                <Text style={styles.marketCardTitle}>📊 UK Market Intelligence</Text>
                <Text style={styles.marketCardSub}>2025–26 recruitment landscape</Text>
              </View>
            </View>
            <View style={styles.marketAlertDot} />
            <MaterialCommunityIcons
              name={showMarketIntelligence ? 'chevron-up' : 'chevron-down'}
              size={20}
              color="#64748b"
            />
          </TouchableOpacity>

          {showMarketIntelligence && (
            <View style={styles.marketCardBody}>
              {/* KPI strip */}
              <View style={styles.marketKpiRow}>
                {[
                  { val: '5.1%', label: 'UK Unemployment', color: '#ef4444' },
                  { val: '2.5x', label: 'Competition ratio', color: '#f97316' },
                  { val: '6', label: 'Key challenges', color: '#3b82f6' },
                ].map((k, i) => (
                  <View key={i} style={[styles.marketKpi, { backgroundColor: k.color + '12' }]}>
                    <Text style={[styles.marketKpiVal, { color: k.color }]}>{k.val}</Text>
                    <Text style={styles.marketKpiLabel}>{k.label}</Text>
                  </View>
                ))}
              </View>

              {/* Top 3 challenges */}
              {[
                { icon: 'account-search-outline', color: '#ef4444', title: 'Skills Shortages', desc: 'Severe lack of qualified applicants in engineering & tech' },
                { icon: 'cash-multiple', color: '#f59e0b', title: 'Rising Hiring Costs', desc: 'Employer NI up 15% + higher National Living Wage' },
                { icon: 'ghost-outline', color: '#64748b', title: 'Ghosting Epidemic', desc: 'Candidates not hearing back — damages employer brand' },
              ].map((item, i) => (
                <View key={i} style={styles.marketChallRow}>
                  <View style={[styles.marketChallIcon, { backgroundColor: item.color + '18' }]}>
                    <MaterialCommunityIcons name={item.icon} size={16} color={item.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.marketChallTitle}>{item.title}</Text>
                    <Text style={styles.marketChallDesc}>{item.desc}</Text>
                  </View>
                </View>
              ))}

              <TouchableOpacity
                style={styles.marketViewAllBtn}
                onPress={() => navigateToScreen('MarketInsights')}
              >
                <MaterialCommunityIcons name="arrow-right-circle" size={16} color="#1e7be2" />
                <Text style={styles.marketViewAllText}>View full market report (5 tabs)</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <View style={styles.activitiesSection}>
          <Text style={styles.sectionTitle}>Recent Activities</Text>
          {notifications.slice(0, 5).map(notification => (
            <View key={notification.id} style={styles.activityItem}>
              <View style={[styles.activityIcon, { backgroundColor: getActivityColor(notification.type) }]}>
                <MaterialCommunityIcons name={getActivityIcon(notification.type)} size={16} color="#fff" />
              </View>
              <View style={styles.activityContent}>
                <Text style={styles.activityTitle}>{notification.title}</Text>
                <Text style={styles.activityMessage}>{notification.message}</Text>
                <Text style={styles.activityTime}>{notification.time}</Text>
              </View>
              <View style={[styles.priorityIndicator, { backgroundColor: getPriorityColor(notification.priority) }]} />
            </View>
          ))}
          <TouchableOpacity style={styles.viewAllButton} onPress={() => setShowNotifications(true)}>
            <Text style={styles.viewAllButtonText}>View All Activities</Text>
            <Ionicons name="chevron-forward" size={16} color="#4A90E2" />
          </TouchableOpacity>
        </View>

        <View style={styles.specializationsSection}>
          <Text style={styles.sectionTitle}>Specializations</Text>
          <View style={styles.specializationsGrid}>
            {(resolvedCompanyProfile?.specializations || effectiveCompanyProfile?.specializations || ['Commercial Construction', 'Residential Development', 'Infrastructure Projects', 'Green Building Solutions']).map((spec, index) => (
              <View key={`spec-${index}`} style={styles.specializationChip}>
                <Text style={styles.specializationText}>{spec}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.locationsSection}>
          <Text style={styles.sectionTitle}>Office Locations</Text>
          <View style={styles.locationCard}>
            <MaterialCommunityIcons name="office-building" size={24} color="#4A90E2" />
            <View style={styles.locationInfo}>
              <Text style={styles.locationName}>London</Text>
              <Text style={styles.locationAddress}>123 Business Street, London EC1A 1BB</Text>
              <Text style={styles.locationEmployees}>45 employees</Text>
            </View>
          </View>
          <View style={styles.locationCard}>
            <MaterialCommunityIcons name="office-building" size={24} color="#4A90E2" />
            <View style={styles.locationInfo}>
              <Text style={styles.locationName}>Manchester</Text>
              <Text style={styles.locationAddress}>456 Industrial Ave, Manchester M1 2AB</Text>
              <Text style={styles.locationEmployees}>20 employees</Text>
            </View>
          </View>
        </View>

        {effectiveSubscription && effectiveSubscription.plan_type === 'multi_user' && (
          <View style={styles.teamOverviewSection}>
            <Text style={styles.sectionTitle}>Team Overview</Text>
            <View style={styles.teamOverview}>
              <View style={styles.teamStat}>
                <Text style={styles.teamStatNumber}>{teamMembers.length}</Text>
                <Text style={styles.teamStatLabel}>Total Members</Text>
              </View>
              <View style={styles.teamStat}>
                <Text style={styles.teamStatNumber}>{teamMembers.filter(m => m.status === 'online').length}</Text>
                <Text style={styles.teamStatLabel}>Online Now</Text>
              </View>
              <View style={styles.teamStat}>
                <Text style={styles.teamStatNumber}>{teamMembers.filter(m => m.department === 'Engineering').length}</Text>
                <Text style={styles.teamStatLabel}>Engineering</Text>
              </View>
            </View>
            <TouchableOpacity style={styles.manageTeamButton} onPress={() => navigateToScreen('TeamManagementScreen', {}, 'team')}>
              <MaterialCommunityIcons name="account-cog" size={16} color="#4A90E2" />
              <Text style={styles.manageTeamButtonText}>Manage Team</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  // Render Recruitment Tab
  const renderRecruitmentTab = () => {
    const recruitmentSolutions = allBusinessSolutions.find(cat => cat.category === 'Recruitment & Talent Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Recruitment Management</Text>
        <View style={styles.recruitmentStats}>
          <View style={styles.recruitmentStatCard}>
            <Text style={styles.recruitmentStatNumber}>{businessStats.activeJobs}</Text>
            <Text style={styles.recruitmentStatLabel}>Active Jobs</Text>
            <TouchableOpacity style={styles.recruitmentStatButton} onPress={() => navigateToScreen('PostJobScreen', {}, 'starter')}>
              <Text style={styles.recruitmentStatButtonText}>Post New Job</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.recruitmentStatCard}>
            <Text style={styles.recruitmentStatNumber}>{businessStats.applications}</Text>
            <Text style={styles.recruitmentStatLabel}>Applications</Text>
            <TouchableOpacity style={styles.recruitmentStatButton} onPress={() => navigateToScreen('ApplicantsPoolScreen', {}, 'starter')}>
              <Text style={styles.recruitmentStatButtonText}>View Applicants</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.recruitmentStatCard}>
            <Text style={styles.recruitmentStatNumber}>{businessStats.interviews}</Text>
            <Text style={styles.recruitmentStatLabel}>Interviews</Text>
            <TouchableOpacity style={styles.recruitmentStatButton} onPress={() => navigateToScreen('InterviewManagementScreen', {}, 'starter')}>
              <Text style={styles.recruitmentStatButtonText}>Schedule Interview</Text>
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {recruitmentSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#4CAF50' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Projects Tab
  const renderProjectsTab = () => {
    const projectSolutions = allBusinessSolutions.find(cat => cat.category === 'Project & Team Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Project & Team Management</Text>
        <View style={styles.projectStats}>
          <View style={styles.projectStatCard}>
            <Text style={styles.projectStatNumber}>{businessStats.projects}</Text>
            <Text style={styles.projectStatLabel}>Active Projects</Text>
          </View>
          <View style={styles.projectStatCard}>
            <Text style={styles.projectStatNumber}>{teamMembers.length || businessStats.teamMembers}</Text>
            <Text style={styles.projectStatLabel}>Team Members</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {projectSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#2196F3' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Vendor Tab
  const renderVendorTab = () => {
    const vendorSolutions = allBusinessSolutions.find(cat => cat.category === 'Vendor & Supply Chain Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Vendor & Supply Chain Management</Text>
        <View style={styles.vendorStats}>
          <View style={styles.vendorStatCard}>
            <Text style={styles.vendorStatNumber}>{businessStats.vendors}</Text>
            <Text style={styles.vendorStatLabel}>Active Vendors</Text>
          </View>
          <View style={styles.vendorStatCard}>
            <Text style={styles.vendorStatNumber}>{businessStats.contracts}</Text>
            <Text style={styles.vendorStatLabel}>Active Contracts</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {vendorSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#FF9800' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Financial Tab
  const renderFinancialTab = () => {
    const financialSolutions = allBusinessSolutions.find(cat => cat.category === 'Financial Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Financial Management</Text>
        {/* Annual Reports */}
        <View style={{flexDirection:'row',gap:10,marginBottom:16}}>
          <TouchableOpacity style={{flex:1,backgroundColor:'#0e4e96',borderRadius:16,padding:16,minHeight:110}} activeOpacity={0.85}
            onPress={() => navigateToScreen('AnnualAuditScreen')}>
            <View style={{width:34,height:34,borderRadius:10,backgroundColor:'rgba(255,255,255,0.15)',alignItems:'center',justifyContent:'center',marginBottom:8}}>
              <MaterialCommunityIcons name="shield-check" size={18} color="#fff" /></View>
            <Text style={{fontSize:15,fontWeight:'900',color:'#fff'}}>Annual Audit</Text>
            <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:3}}>P&L, Balance Sheet, Tax, Ratios</Text>
            <View style={{flexDirection:'row',alignItems:'center',marginTop:6}}>
              <Text style={{fontSize:9,color:'#F5A623',fontWeight:'700'}}>View Report </Text>
              <MaterialCommunityIcons name="chevron-right" size={12} color="#F5A623" /></View>
          </TouchableOpacity>
          <TouchableOpacity style={{flex:1,backgroundColor:'#10B981',borderRadius:16,padding:16,minHeight:110}} activeOpacity={0.85}
            onPress={() => navigateToScreen('AnnualRevenueScreen')}>
            <View style={{width:34,height:34,borderRadius:10,backgroundColor:'rgba(255,255,255,0.15)',alignItems:'center',justifyContent:'center',marginBottom:8}}>
              <MaterialCommunityIcons name="chart-line" size={18} color="#fff" /></View>
            <Text style={{fontSize:15,fontWeight:'900',color:'#fff'}}>Annual Revenue</Text>
            <Text style={{fontSize:9,color:'rgba(255,255,255,0.6)',marginTop:3}}>MRR, ARR, Clients, Forecast</Text>
            <View style={{flexDirection:'row',alignItems:'center',marginTop:6}}>
              <Text style={{fontSize:9,color:'#FDE68A',fontWeight:'700'}}>View Report </Text>
              <MaterialCommunityIcons name="chevron-right" size={12} color="#FDE68A" /></View>
          </TouchableOpacity>
        </View>

        {/* Financial KPIs */}
        <View style={{flexDirection:'row',gap:8,marginBottom:16}}>
          <TouchableOpacity style={{flex:1,backgroundColor:'#fff',borderRadius:14,padding:14,borderWidth:1,borderColor:'#E2E8F0',alignItems:'center'}}
            onPress={() => navigateToScreen('AnnualRevenueScreen')}>
            <MaterialCommunityIcons name="trending-up" size={20} color="#10B981" />
            <Text style={{fontSize:18,fontWeight:'900',color:'#10B981',marginTop:4}}>
              <Text>{businessStats.revenue > 0 ? '£' + (businessStats.revenue / 1000).toFixed(0) + 'K' : '£0K'}</Text>
            </Text>
            <Text style={{fontSize:9,color:'#64748B'}}>Revenue</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{flex:1,backgroundColor:'#fff',borderRadius:14,padding:14,borderWidth:1,borderColor:'#E2E8F0',alignItems:'center'}}
            onPress={() => navigateToScreen('FinancialManagement')}>
            <MaterialCommunityIcons name="receipt" size={20} color="#F59E0B" />
            <Text style={{fontSize:18,fontWeight:'900',color:'#F59E0B',marginTop:4}}>{businessStats.invoices || 0}</Text>
            <Text style={{fontSize:9,color:'#64748B'}}>Invoices</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{flex:1,backgroundColor:'#fff',borderRadius:14,padding:14,borderWidth:1,borderColor:'#E2E8F0',alignItems:'center'}}
            onPress={() => navigateToScreen('FinancialAnalyticsDashboard')}>
            <MaterialCommunityIcons name="chart-pie" size={20} color="#8B5CF6" />
            <Text style={{fontSize:18,fontWeight:'900',color:'#8B5CF6',marginTop:4}}>Analytics</Text>
            <Text style={{fontSize:9,color:'#64748B'}}>Dashboard</Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.sectionTitle}>All Financial Features</Text>
        <View style={styles.featuresGrid}>
          {financialSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#4CAF50' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Marketing Tab
  const renderMarketingTab = () => {
    const marketingSolutions = allBusinessSolutions.find(cat => cat.category === 'Marketing & Campaigns')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Marketing & Campaigns</Text>
        <View style={styles.marketingStats}>
          <View style={styles.marketingStatCard}>
            <Text style={styles.marketingStatNumber}>{businessStats.campaigns}</Text>
            <Text style={styles.marketingStatLabel}>Active Campaigns</Text>
          </View>
          <View style={styles.marketingStatCard}>
            <Text style={styles.marketingStatNumber}>{businessStats.clients}</Text>
            <Text style={styles.marketingStatLabel}>Active Clients</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {marketingSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#E91E63' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Compliance Tab
  const renderComplianceTab = () => {
    const complianceSolutions = allBusinessSolutions.find(cat => cat.category === 'Compliance & Regulatory')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Compliance & Regulatory</Text>
        <View style={styles.complianceInfo}>
          <View style={styles.complianceCard}>
            <MaterialCommunityIcons name="shield-check" size={32} color="#4CAF50" />
            <Text style={styles.complianceTitle}>Compliance Status</Text>
            <Text style={styles.complianceStatus}>Up to Date</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {complianceSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#9C27B0' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Analytics Tab
  const renderAnalyticsTab = () => {
    const analyticsSolutions = allBusinessSolutions.find(cat => cat.category === 'Analytics & Reporting')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Analytics & Reporting</Text>
        <View style={styles.analyticsOverview}>
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsNumber}>+12.5%</Text>
            <Text style={styles.analyticsLabel}>Revenue Growth</Text>
          </View>
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsNumber}>+8%</Text>
            <Text style={styles.analyticsLabel}>Client Growth</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {analyticsSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#795548' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Clients Tab
  const renderClientsTab = () => {
    const clientsSolutions = allBusinessSolutions.find(cat => cat.category === 'Client & Customer Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Client & Customer Management</Text>
        <View style={styles.clientsStats}>
          <View style={styles.clientsStatCard}>
            <Text style={styles.clientsStatNumber}>{businessStats.clients}</Text>
            <Text style={styles.clientsStatLabel}>Active Clients</Text>
          </View>
          <View style={styles.clientsStatCard}>
            <Text style={styles.clientsStatNumber}>{businessStats.projects}</Text>
            <Text style={styles.clientsStatLabel}>Ongoing Projects</Text>
          </View>
        </View>
        <View style={styles.featuresGrid}>
          {clientsSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#3fb5afff' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // Render Company Management Tab
  const renderCompanyManagmentTab = () => {
    const companyManagementSolutions = allBusinessSolutions.find(cat => cat.category === 'Company Management')?.solutions || [];
    return (
      <View style={styles.tabContent}>
        <Text style={styles.sectionTitle}>Company Management</Text>
        <View style={styles.featuresGrid}>
          {companyManagementSolutions.map((solution, index) => {
            const available = isFeatureAvailable(solution.requiredPlan);
            return (
              <TouchableOpacity
                key={`${solution.screen}-${solution.title}`}
                style={[styles.featureCard, !available && styles.lockedFeatureCard]}
                onPress={() => {
                  if (!available) {
                    Alert.alert(
                      'Upgrade Required',
                      `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                      [
                        { text: 'Cancel', style: 'cancel' },
                        { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                      ]
                    );
                  } else {
                    navigateToScreen(solution.screen);
                  }
                }}
              >
                <MaterialCommunityIcons name={solution.icon} size={28} color={available ? '#3F51B5' : '#94A3B8'} />
                {!available && <View style={styles.featureLockIcon}><Ionicons name="lock-closed" size={16} color="#94A3B8" /></View>}
                <Text style={styles.featureTitle}>{solution.title}</Text>
                <Text style={styles.featureDescription}>{solution.description}</Text>
                {!available && (
                  <View style={styles.planRequiredBadge}>
                    <Text style={styles.planRequiredText}>{String(solution.requiredPlan ?? '').toUpperCase()}</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };


  // Render Market Insights Tab
  const renderMarketInsightsTab = () => {
    const challenges = [
      { icon: 'chart-line-variant', color: '#ef4444', title: 'Market Slowdown', desc: 'Vacancies stagnant despite high applicants. Companies "just browsing" without committing.', severity: 'Critical' },
      { icon: 'account-group-outline', color: '#f97316', title: 'Rising Competition', desc: '2.5 unemployed per vacancy (up from 1.8). Job seekers face intense competition.', severity: 'Critical' },
      { icon: 'airplane-takeoff', color: '#8b5cf6', title: 'Offshoring Acceleration', desc: 'Insurance & finance reducing UK headcount in 2025. Structural job losses, not cyclical.', severity: 'High' },
      { icon: 'school-outline', color: '#3b82f6', title: 'Entry-Level Crisis', desc: 'Graduate roles disappearing. Entry-level jobs now require 3+ years experience.', severity: 'Critical' },
      { icon: 'ghost-outline', color: '#64748b', title: 'Ghosting Epidemic', desc: 'Candidates ghosted after interviews. Normalised behaviour damaging employer brands.', severity: 'High' },
      { icon: 'cash-multiple', color: '#f59e0b', title: 'Rising Employment Costs', desc: 'Employer NI up to 15%, higher National Living Wage. Cost of hiring up across all sectors.', severity: 'High' },
    ];

    const bestPractices = [
      { num: '01', title: 'Always Post Salary Ranges', desc: 'No salary = low engagement. Top reason candidates ignore job posts.', color: '#0e4e96' },
      { num: '02', title: 'Max 3 Interview Rounds', desc: '1 phone screen + 1 interview for most roles. Stop losing candidates to faster competitors.', color: '#7c3aed' },
      { num: '03', title: 'Stop Ghosting Candidates', desc: 'Every applicant deserves closure. Automated status emails protect your brand.', color: '#059669' },
      { num: '04', title: 'Prioritise Referrals', desc: 'Employee referrals are your highest quality hires. Build a referral programme.', color: '#d97706' },
      { num: '05', title: 'Recruiter–Manager Alignment', desc: 'Agree must-haves vs nice-to-haves before starting search. Avoid chasing perfect candidates.', color: '#dc2626' },
    ];

    const severityColor = (s) => s === 'Critical' ? '#ef4444' : '#f97316';

    return (
      <View style={styles.tabContent}>
        {/* Header */}
        <View style={[styles.insightsBanner, { backgroundColor: '#0e4e96' }]}>
          <MaterialCommunityIcons name="trending-up" size={20} color="#fff" />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={styles.insightsBannerTitle}>UK Recruitment Intelligence 2025–26</Text>
            <Text style={styles.insightsBannerSub}>Based on CIPD, Reddit r/UKJobs, r/Recruitment · April 2026</Text>
          </View>
          <TouchableOpacity
            style={styles.insightsFullBtn}
            onPress={() => navigateToScreen('MarketInsights')}
          >
            <Text style={styles.insightsFullBtnText}>Full Report</Text>
            <MaterialCommunityIcons name="arrow-right" size={14} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* KPI Strip */}
        <View style={styles.insightsKpiRow}>
          {[
            { val: '5.1%', label: 'Unemployment', color: '#ef4444' },
            { val: '2.5x', label: 'Per Vacancy', color: '#f97316' },
            { val: '+15%', label: 'Employer NI', color: '#8b5cf6' },
            { val: '68%', label: 'Want Hybrid', color: '#3b82f6' },
          ].map((k, i) => (
            <View key={i} style={[styles.insightsKpi, { borderTopColor: k.color }]}>
              <Text style={[styles.insightsKpiVal, { color: k.color }]}>{k.val}</Text>
              <Text style={styles.insightsKpiLabel}>{k.label}</Text>
            </View>
          ))}
        </View>

        {/* Challenges */}
        <Text style={styles.sectionTitle}>Key Recruitment Challenges</Text>
        {challenges.map((c, i) => (
          <View key={i} style={styles.insightsChallengeCard}>
            <View style={[styles.marketChallIcon, { backgroundColor: c.color + '18' }]}>
              <MaterialCommunityIcons name={c.icon} size={18} color={c.color} />
            </View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <Text style={styles.insightsChallengeTitle}>{c.title}</Text>
                <View style={[styles.severityBadge, { backgroundColor: severityColor(c.severity) + '20' }]}>
                  <Text style={[styles.severityText, { color: severityColor(c.severity) }]}>{c.severity}</Text>
                </View>
              </View>
              <Text style={styles.insightsChallengeDesc}>{c.desc}</Text>
            </View>
          </View>
        ))}

        {/* Best Practices */}
        <Text style={styles.sectionTitle}>Community Best Practices</Text>
        {bestPractices.map((bp, i) => (
          <View key={i} style={[styles.insightsBPCard, { borderLeftColor: bp.color }]}>
            <View style={[styles.insightsBPNum, { backgroundColor: bp.color }]}>
              <Text style={styles.insightsBPNumText}>{bp.num}</Text>
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={styles.insightsBPTitle}>{bp.title}</Text>
              <Text style={styles.insightsBPDesc}>{bp.desc}</Text>
            </View>
          </View>
        ))}

        {/* JobEng response */}
        <Text style={styles.sectionTitle}>How JobEng Addresses These</Text>
        <View style={styles.insightsResponseGrid}>
          {[
            { icon: 'robot', color: '#6366f1', label: 'AI Matching', desc: 'Skills-based matching reduces screening time' },
            { icon: 'email-fast', color: '#0e4e96', label: 'Auto Comms', desc: 'No more ghosting — automated status emails' },
            { icon: 'shield-check', color: '#059669', label: 'IR35 Compliance', desc: '35 UK contract types, fully compliant' },
            { icon: 'chart-bar', color: '#d97706', label: 'Cost Analytics', desc: 'Track cost-per-hire vs NI thresholds' },
          ].map((r, i) => (
            <View key={i} style={styles.insightsResponseCard}>
              <View style={[styles.insightsResponseIcon, { backgroundColor: r.color }]}>
                <MaterialCommunityIcons name={r.icon} size={20} color="#fff" />
              </View>
              <Text style={styles.insightsResponseLabel}>{r.label}</Text>
              <Text style={styles.insightsResponseDesc}>{r.desc}</Text>
            </View>
          ))}
        </View>

        <TouchableOpacity
          style={styles.insightsFullReportBtn}
          onPress={() => navigateToScreen('MarketInsights')}
        >
          <MaterialCommunityIcons name="file-chart" size={18} color="#fff" />
          <Text style={styles.insightsFullReportBtnText}>Open Full Market Report</Text>
          <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
        </TouchableOpacity>
      </View>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverviewTab();
      case 'recruitment': return renderRecruitmentTab();
      case 'projects': return renderProjectsTab();
      case 'vendor': return renderVendorTab();
      case 'financial': return renderFinancialTab();
      case 'marketing': return renderMarketingTab();
      case 'compliance': return renderComplianceTab();
      case 'analytics': return renderAnalyticsTab();
      case 'company management': return renderCompanyManagmentTab();
      case 'clients': return renderClientsTab();
      case 'insights': return renderMarketInsightsTab();
      default: return renderOverviewTab();
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading your business dashboard...</Text>
          <Text style={styles.loadingSubtext}>Syncing with database...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <LinearGradient colors={['#4A90E2', '#4A90E2']} style={styles.fullScreenContainer}>
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      <SafeAreaView style={styles.safeContainer}>
        {renderHeader()}
        <View style={styles.contentContainer}>
          {renderTabNavigation()}
          <ScrollView
            style={styles.content}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
            showsVerticalScrollIndicator={false}
          >
            {renderTabContent()}
          </ScrollView>
        </View>

        {/* Floating Action Button */}
        <TouchableOpacity style={styles.fab} onPress={() => setShowQuickActions(true)}>
          <Ionicons name="add" size={24} color="#fff" />
        </TouchableOpacity>

        {/* Search Modal */}
        <Modal animationType="slide" transparent visible={showSearchModal} onRequestClose={() => setShowSearchModal(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.searchModalContainer}>
              <View style={styles.searchHeader}>
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search business solutions..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  autoFocus
                  placeholderTextColor="#94A3B8"
                />
                <TouchableOpacity onPress={() => setShowSearchModal(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.searchResults}>
                {allBusinessSolutions.map(category =>
                  category.solutions
                    .filter(solution => String(solution.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) || String(solution.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((solution, index) => {
                      const available = isFeatureAvailable(solution.requiredPlan);
                      return (
                        <TouchableOpacity
                          key={`${category.category}-${index}`}
                          style={[styles.searchResultItem, !available && styles.lockedSearchResult]}
                          onPress={() => {
                            setShowSearchModal(false);
                            if (!available) {
                              Alert.alert(
                                'Upgrade Required',
                                `This feature requires ${solution.requiredPlan} plan. Upgrade to access.`,
                                [
                                  { text: 'Cancel', style: 'cancel' },
                                  { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                                ]
                              );
                            } else {
                              navigateToScreen(solution.screen);
                            }
                          }}
                        >
                          <MaterialCommunityIcons name={solution.icon} size={20} color={available ? category.color : '#94A3B8'} />
                          <View style={styles.searchResultContent}>
                            <Text style={[styles.searchResultTitle, !available && styles.lockedSearchResultTitle]}>{solution.title}</Text>
                            <Text style={styles.searchResultCategory}>{category.category}</Text>
                          </View>
                          {!available && <Ionicons name="lock-closed" size={16} color="#94A3B8" />}
                        </TouchableOpacity>
                      );
                    })
                )}
              </ScrollView>
            </View>
          </View>
        </Modal>

        {/* Notifications Modal */}
        <Modal animationType="slide" transparent visible={showNotifications} onRequestClose={() => setShowNotifications(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Notifications</Text>
                <TouchableOpacity onPress={() => setShowNotifications(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.modalContent}>
                {notifications.length > 0 ? (
                  notifications.map(notification => (
                    <View key={notification.id} style={styles.notificationItem}>
                      <View style={[styles.notificationIcon, { backgroundColor: getActivityColor(notification.type) }]}>
                        <MaterialCommunityIcons name={getActivityIcon(notification.type)} size={16} color="#fff" />
                      </View>
                      <View style={styles.notificationContent}>
                        <View style={styles.notificationHeader}>
                          <Text style={styles.notificationTitle}>{notification.title}</Text>
                          <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(notification.priority) }]}>
                            <Text style={styles.priorityText}>{String(notification.priority ?? '').toUpperCase()}</Text>
                          </View>
                        </View>
                        <Text style={styles.notificationMessage}>{notification.message}</Text>
                        <Text style={styles.notificationTime}>{notification.time}</Text>
                      </View>
                    </View>
                  ))
                ) : (
                  <View style={styles.emptyNotifications}>
                    <MaterialCommunityIcons name="bell-off-outline" size={48} color="#94A3B8" />
                    <Text style={styles.emptyNotificationsText}>No notifications</Text>
                  </View>
                )}
              </ScrollView>
            </View>
          </View>
        </Modal>

        {/* Quick Actions Modal */}
        <Modal animationType="slide" transparent visible={showQuickActions} onRequestClose={() => setShowQuickActions(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Quick Actions</Text>
                <TouchableOpacity onPress={() => setShowQuickActions(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <View style={styles.modalContent}>
                <View style={styles.quickActionsModalGrid}>
                  {[
                    { title: 'Post Job', icon: 'briefcase-plus', color: '#2196F3', screen: 'PostJobScreen', requiredPlan: 'starter' },
                    { title: 'Schedule Interview', icon: 'calendar-plus', color: '#FF9800', screen: 'InterviewManagementScreen', requiredPlan: 'starter' },
                    { title: 'Add Project', icon: 'clipboard-plus', color: '#9C27B0', screen: 'AddProjectScreen', requiredPlan: 'professional' },
                    { title: 'Create Invoice', icon: 'file-plus', color: '#4CAF50', screen: 'CreateInvoiceScreen', requiredPlan: 'professional' },
                    { title: 'Add Team Member', icon: 'account-plus', color: '#E91E63', screen: 'TeamManagementScreen', requiredPlan: 'team' },
                    { title: 'Marketing Campaign', icon: 'bullhorn', color: '#FF5722', screen: 'CreateCampaignScreen', requiredPlan: 'enterprise' },
                    { title: 'Payment Center', icon: 'credit-card', color: BRAND.blue, screen: 'BusinessPaymentSystem', requiredPlan: 'starter' },
                    { title: 'Compliance Check', icon: 'shield-check', color: '#9C27B0', screen: 'ComplianceCheckScreen', requiredPlan: 'professional' },
                    { title: 'Video Conference', icon: 'video', color: '#00BCD4', screen: 'BusinessVideoConferenceScreen', requiredPlan: 'professional' },
                    { title: 'Analytics Report', icon: 'chart-line', color: '#795548', screen: 'CompanyAnalytics', requiredPlan: 'enterprise' },
                  ].map((action, index) => {
                    const available = isFeatureAvailable(action.requiredPlan);
                    return (
                      <TouchableOpacity
                        key={action.title}
                        style={[styles.quickActionModalCard, !available && styles.lockedQuickActionModalCard]}
                        onPress={() => {
                          setShowQuickActions(false);
                          if (!available) {
                            Alert.alert(
                              'Upgrade Required',
                              `This feature requires ${action.requiredPlan} plan. Upgrade to access.`,
                              [
                                { text: 'Cancel', style: 'cancel' },
                                { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                              ]
                            );
                          } else {
                            navigateToScreen(action.screen);
                          }
                        }}
                      >
                        <View style={[styles.quickActionModalIcon, { backgroundColor: available ? action.color : '#94A3B8' }]}>
                          <MaterialCommunityIcons name={action.icon} size={24} color="#fff" />
                          {!available && (
                            <View style={styles.quickActionLockOverlay}>
                              <Ionicons name="lock-closed" size={12} color="#fff" />
                            </View>
                          )}
                        </View>
                        <Text style={[styles.quickActionModalText, !available && styles.lockedQuickActionModalText]}>{action.title}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* Team Modal */}
        <Modal animationType="slide" transparent visible={showTeamModal} onRequestClose={() => setShowTeamModal(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Team Members</Text>
                <TouchableOpacity onPress={() => setShowTeamModal(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <View style={styles.modalContent}>
                <View style={styles.teamStats}>
                  <Text style={styles.teamStatsText}>
                    {teamMembers.length} team members • {teamMembers.filter(m => m.status === 'online').length} online
                  </Text>
                </View>
                {teamMembers.length > 0 ? (
                  teamMembers.map((member, index) => (
                    <View key={member.id || member.email || `tm-${index}`} style={styles.teamMemberItem}>
                      <View style={styles.teamMemberAvatar}>
                        <Text style={styles.teamMemberInitials}>{getUserInitials(member.name)}</Text>
                      </View>
                      <View style={styles.teamMemberInfo}>
                        <Text style={styles.teamMemberName}>{member.name}</Text>
                        <Text style={styles.teamMemberRole}>{member.role} • {member.department}</Text>
                        <Text style={styles.teamMemberEmail}>{member.email}</Text>
                      </View>
                      <View style={[styles.teamMemberStatus, { backgroundColor: member.status === 'online' ? '#4CAF50' : member.status === 'away' ? '#FF9800' : '#94A3B8' }]} />
                    </View>
                  ))
                ) : (
                  <View style={styles.emptyTeam}>
                    <MaterialCommunityIcons name="account-group" size={48} color="#94A3B8" />
                    <Text style={styles.emptyTeamText}>No team members added yet</Text>
                    <TouchableOpacity style={styles.addTeamButton} onPress={() => { setShowTeamModal(false); navigateToScreen('TeamManagementScreen'); }}>
                      <Text style={styles.addTeamButtonText}>Add Team Members</Text>
                    </TouchableOpacity>
                  </View>
                )}
                <TouchableOpacity style={styles.manageTeamButton} onPress={() => { setShowTeamModal(false); navigateToScreen('TeamManagementScreen'); }}>
                  <MaterialCommunityIcons name="account-cog" size={20} color="#4A90E2" />
                  <Text style={styles.manageTeamButtonText}>Manage Team</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Business Solutions Modal */}
        <Modal animationType="slide" transparent visible={showBusinessSolutions} onRequestClose={() => setShowBusinessSolutions(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.fullModalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>All Business Solutions</Text>
                <TouchableOpacity onPress={() => setShowBusinessSolutions(false)}>
                  <Ionicons name="close" size={24} color="#64748B" />
                </TouchableOpacity>
              </View>
              <View style={styles.solutionsOverview}>
                <Text style={styles.solutionsCount}>{allBusinessSolutions.reduce((total, cat) => total + cat.solutions.length, 0)} Solutions</Text>
                <Text style={styles.solutionsCategories}>{allBusinessSolutions.length} Categories</Text>
              </View>
              <SectionList
                sections={allBusinessSolutions.map(category => ({
                  title: category.category,
                  data: category.solutions,
                  icon: category.icon,
                  color: category.color,
                  requiredPlan: category.requiredPlan,
                }))}
                keyExtractor={(item, index) => `${item.title}-${index}`}
                renderItem={({ item, section }) => {
                  const available = isFeatureAvailable(item.requiredPlan);
                  return (
                    <TouchableOpacity
                      style={[styles.solutionModalItem, !available && styles.lockedSolutionModalItem]}
                      onPress={() => {
                        setShowBusinessSolutions(false);
                        if (!available) {
                          Alert.alert(
                            'Upgrade Required',
                            `This feature requires ${item.requiredPlan} plan. Upgrade to access.`,
                            [
                              { text: 'Cancel', style: 'cancel' },
                              { text: 'Upgrade', onPress: handleSubscriptionUpgrade },
                            ]
                          );
                        } else {
                          navigateToScreen(item.screen);
                        }
                      }}
                    >
                      <View style={[styles.solutionModalIcon, { backgroundColor: `${section.color}20` }]}>
                        <MaterialCommunityIcons name={item.icon} size={20} color={available ? section.color : '#94A3B8'} />
                        {!available && (
                          <View style={styles.solutionModalLock}>
                            <Ionicons name="lock-closed" size={12} color="#94A3B8" />
                          </View>
                        )}
                      </View>
                      <View style={styles.solutionModalContent}>
                        <Text style={[styles.solutionModalTitle, !available && styles.lockedSolutionModalTitle]}>{item.title}</Text>
                        <Text style={styles.solutionModalDescription}>{item.description}</Text>
                      </View>
                      {!available && (
                        <View style={styles.solutionPlanBadge}>
                          <Text style={styles.solutionPlanBadgeText}>{String(item.requiredPlan ?? '').toUpperCase()}</Text>
                        </View>
                      )}
                      <Ionicons name="chevron-forward" size={16} color="#94A3B8" />
                    </TouchableOpacity>
                  );
                }}
                renderSectionHeader={({ section }) => {
                  const available = isFeatureAvailable(section.requiredPlan);
                  return (
                    <View style={styles.solutionSectionHeader}>
                      <View style={[styles.solutionSectionIcon, { backgroundColor: `${section.color}20` }]}>
                        <MaterialCommunityIcons name={section.icon} size={24} color={available ? section.color : '#94A3B8'} />
                      </View>
                      <View style={styles.solutionSectionContent}>
                        <Text style={styles.solutionSectionTitle}>{section.title}</Text>
                        <Text style={styles.solutionSectionCount}>{section.data.length} solutions</Text>
                      </View>
                      {!available && (
                        <View style={styles.sectionPlanBadge}>
                          <Text style={styles.sectionPlanBadgeText}>{String(section.requiredPlan ?? '').toUpperCase()}</Text>
                        </View>
                      )}
                    </View>
                  );
                }}
                showsVerticalScrollIndicator={false}
                stickySectionHeadersEnabled
                style={styles.solutionsList}
              />
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    </LinearGradient>
  );
}

// ==================== STYLES ====================
const styles = StyleSheet.create({
  fullScreenContainer: { flex: 1 },
  safeContainer: { flex: 1, backgroundColor: 'transparent' },
  contentContainer: { flex: 1, backgroundColor: '#F9FAFB', borderTopLeftRadius: 24, borderTopRightRadius: 24, overflow: 'hidden' },
  safeArea: { flex: 1, backgroundColor: '#3B82F6' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  loadingText: { fontSize: 18, fontWeight: '600', color: '#0F172A', marginTop: 16 },
  loadingSubtext: { fontSize: 14, color: '#64748B', marginTop: 8, textAlign: 'center' },
  header: { paddingTop: 10, paddingBottom: 15, paddingHorizontal: 20 },
  headerContent: { flexDirection: 'row', alignItems: 'center', marginBottom: 15 },
  logoContainer: { width: 50, height: 50, borderRadius: 25, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginRight: 12, position: 'relative', overflow: 'visible' },
  logoImage: { width: 50, height: 50, borderRadius: 25, borderWidth: 2, borderColor: 'rgba(255,255,255,0.4)' },
  logoInitialsWrap: { width: 50, height: 50, borderRadius: 25, backgroundColor: 'rgba(255,255,255,0.15)', borderWidth: 2, borderColor: 'rgba(255,255,255,0.4)', alignItems: 'center', justifyContent: 'center' },
  logoInitialsText: { color: '#fff', fontSize: 16, fontWeight: '900' },
  logoDot: { position: 'absolute', bottom: 2, right: 2, width: 10, height: 10, borderRadius: 5, backgroundColor: '#fdb913', borderWidth: 2, borderColor: '#4A90E2' },
  companyInfo: { flex: 1 },
  companyName: { fontSize: 18, fontWeight: '700', color: '#fff', marginBottom: 2 },
  companyType: { fontSize: 14, color: 'rgba(255,255,255,0.8)', marginBottom: 8 },
  subscriptionStatusHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4, marginBottom: 8, alignSelf: 'flex-start' },
  subscriptionStatusText: { fontSize: 10, fontWeight: '600', marginLeft: 4 },
  manageSubscriptionButton: { backgroundColor: '#FFD700', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2, marginLeft: 8 },
  manageSubscriptionText: { fontSize: 10, fontWeight: '600', color: '#92400e' },
  companyStats: { flexDirection: 'row', gap: 16 },
  statItem: { alignItems: 'center' },
  statNumber: { fontSize: 16, fontWeight: '700', color: '#fff' },
  statLabel: { fontSize: 11, color: 'rgba(255,255,255,0.7)' },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerButton: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  notificationBadge: { position: 'absolute', top: -2, right: -2, width: 16, height: 16, borderRadius: 8, backgroundColor: '#E91E63', alignItems: 'center', justifyContent: 'center' },
  notificationBadgeText: { fontSize: 10, fontWeight: '600', color: '#fff' },
  profileButton: { width: 36, height: 36, borderRadius: 18, overflow: 'hidden' },
  profileAvatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  profileInitials: { fontSize: 14, fontWeight: '600', color: '#fff' },
  metricsContainer: { flexDirection: 'row', gap: 12, marginBottom: 15 },
  metricCard: { flex: 1, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: 12 },
  metricValue: { fontSize: 20, fontWeight: '700', color: '#fff', marginBottom: 2 },
  metricLabel: { fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 },
  metricChange: { flexDirection: 'row', alignItems: 'center' },
  metricChangeText: { fontSize: 11, color: '#10b981', marginLeft: 2, fontWeight: '600' },
  actionButtons: { flexDirection: 'row', gap: 8 },
  actionButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  actionButtonText: { fontSize: 12, fontWeight: '600', color: '#fff', marginLeft: 4 },
  tabNavigation: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, marginRight: 8 },
  activeTab: { borderBottomWidth: 2, borderBottomColor: '#4A90E2' },
  tabText: { fontSize: 13, fontWeight: '500', color: '#64748B', marginLeft: 6 },
  activeTabText: { color: '#4A90E2', fontWeight: '600' },
  content: { flex: 1 },
  tabContent: { padding: 20 },
  welcomeSection: { marginBottom: 24 },
  welcomeTitle: { fontSize: 24, fontWeight: '700', color: '#0F172A', marginBottom: 4 },
  welcomeSubtitle: { fontSize: 16, color: '#64748B', marginBottom: 12 },
  subscriptionBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FEF3C7', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6, alignSelf: 'flex-start' },
  subscriptionText: { fontSize: 12, fontWeight: '600', marginLeft: 4 },
  upgradeInlineButton: { backgroundColor: '#4A90E2', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4, marginLeft: 8 },
  upgradeInlineButtonText: { fontSize: 10, fontWeight: '600', color: '#fff' },
  manageInlineButton: { backgroundColor: '#4A90E2', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4, marginLeft: 8 },
  manageInlineButtonText: { fontSize: 10, fontWeight: '600', color: '#fff' },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  sectionTitle: { fontSize: 20, fontWeight: '600', color: '#0F172A', marginBottom: 16 },
  upgradeSectionButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFD700', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  upgradeSectionButtonText: { fontSize: 12, fontWeight: '600', color: '#92400e', marginLeft: 4 },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  statCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, width: (width - 52) / 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  statIcon: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  statCardNumber: { fontSize: 24, fontWeight: '700', color: '#0F172A', marginBottom: 4 },
  statCardLabel: { fontSize: 14, color: '#64748B', marginBottom: 12 },
  statCardAction: { backgroundColor: '#F1F5F9', borderRadius: 6, paddingVertical: 6, paddingHorizontal: 12, alignItems: 'center' },
  statCardActionText: { fontSize: 12, fontWeight: '600', color: '#4A90E2' },
  quickActionsSection: { marginBottom: 24 },
  quickActionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  quickActionCard: { backgroundColor: '#fff', borderRadius: 12, padding: 12, width: (width - 68) / 3, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 1 },
  lockedActionCard: { opacity: 0.6 },
  quickActionIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', marginBottom: 8, position: 'relative' },
  lockOverlay: { position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 8, backgroundColor: '#ef4444', justifyContent: 'center', alignItems: 'center' },
  quickActionText: { fontSize: 10, fontWeight: '500', color: '#0F172A', textAlign: 'center' },
  lockedActionText: { color: '#94A3B8' },
  quickActionLockBadge: { backgroundColor: '#ef4444', borderRadius: 4, paddingHorizontal: 4, paddingVertical: 2, marginTop: 4 },
  quickActionLockText: { fontSize: 8, fontWeight: '600', color: '#fff' },
  subscriptionBenefitsSection: { backgroundColor: '#FFFBEB', borderRadius: 12, padding: 20, marginBottom: 24, borderWidth: 1, borderColor: '#FEF3C7' },
  benefitsHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  benefitsTitle: { fontSize: 18, fontWeight: '600', color: '#92400e', marginLeft: 8 },
  benefitsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  benefitItem: { flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: '45%', marginBottom: 8 },
  benefitText: { fontSize: 12, color: '#92400e', marginLeft: 8, fontWeight: '500' },
  upgradeCtaButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4A90E2', borderRadius: 8, paddingVertical: 12, paddingHorizontal: 16 },
  upgradeCtaButtonText: { fontSize: 14, fontWeight: '600', color: '#fff', marginLeft: 8 },
  activitiesSection: { marginBottom: 24 },
  activityItem: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 1, position: 'relative' },
  activityIcon: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  activityContent: { flex: 1 },
  activityTitle: { fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  activityMessage: { fontSize: 13, color: '#64748B', marginBottom: 4 },
  activityTime: { fontSize: 11, color: '#94A3B8' },
  priorityIndicator: { position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: 4 },
  viewAllButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', borderRadius: 8, paddingVertical: 12, marginTop: 8 },
  viewAllButtonText: { fontSize: 14, fontWeight: '600', color: '#4A90E2', marginRight: 4 },
  specializationsSection: { marginBottom: 24 },
  specializationsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  specializationChip: { backgroundColor: '#E3F2FD', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6 },
  specializationText: { fontSize: 12, fontWeight: '500', color: '#1976D2' },
  locationsSection: { marginBottom: 24 },
  locationCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 1 },
  locationInfo: { flex: 1, marginLeft: 12 },
  locationName: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  locationAddress: { fontSize: 13, color: '#64748B', marginBottom: 2 },
  locationEmployees: { fontSize: 12, color: '#94A3B8' },
  teamOverviewSection: { backgroundColor: '#fff', borderRadius: 12, padding: 20, marginBottom: 24, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  teamOverview: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 },
  teamStat: { alignItems: 'center', flex: 1 },
  teamStatNumber: { fontSize: 24, fontWeight: '700', color: '#4A90E2', marginBottom: 4 },
  teamStatLabel: { fontSize: 12, color: '#64748B', textAlign: 'center' },
  manageTeamButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', borderRadius: 8, paddingVertical: 12 },
  manageTeamButtonText: { fontSize: 14, fontWeight: '600', color: '#4A90E2', marginLeft: 8 },
  recruitmentStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  recruitmentStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  recruitmentStatNumber: { fontSize: 28, fontWeight: '700', color: '#4CAF50', marginBottom: 4 },
  recruitmentStatLabel: { fontSize: 14, color: '#64748B', marginBottom: 12 },
  recruitmentStatButton: { backgroundColor: '#4CAF50', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 },
  recruitmentStatButtonText: { fontSize: 12, fontWeight: '600', color: '#fff' },
  featuresGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  featureCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, width: (width - 52) / 2, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2, position: 'relative' },
  lockedFeatureCard: { opacity: 0.6 },
  featureLockIcon: { position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 10, padding: 4 },
  featureTitle: { fontSize: 13, fontWeight: '600', color: '#0F172A', marginTop: 12, marginBottom: 4, textAlign: 'center' },
  featureDescription: { fontSize: 11, color: '#64748B', textAlign: 'center', lineHeight: 14 },
  planRequiredBadge: { backgroundColor: '#fef3c7', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2, marginTop: 8 },
  planRequiredText: { fontSize: 8, fontWeight: '600', color: '#92400e' },
  projectStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  projectStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  projectStatNumber: { fontSize: 28, fontWeight: '700', color: '#2196F3', marginBottom: 4 },
  projectStatLabel: { fontSize: 14, color: '#64748B' },
  vendorStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  vendorStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  vendorStatNumber: { fontSize: 28, fontWeight: '700', color: '#FF9800', marginBottom: 4 },
  vendorStatLabel: { fontSize: 14, color: '#64748B' },
  financialStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  financialStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  financialStatNumber: { fontSize: 28, fontWeight: '700', color: '#4CAF50', marginBottom: 4 },
  financialStatLabel: { fontSize: 14, color: '#64748B' },
  marketingStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  marketingStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  marketingStatNumber: { fontSize: 28, fontWeight: '700', color: '#E91E63', marginBottom: 4 },
  marketingStatLabel: { fontSize: 14, color: '#64748B' },
  complianceInfo: { marginBottom: 24 },
  complianceCard: { backgroundColor: '#fff', borderRadius: 12, padding: 20, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  complianceTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginTop: 12, marginBottom: 4 },
  complianceStatus: { fontSize: 14, color: '#4CAF50', fontWeight: '500' },
  analyticsOverview: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  analyticsCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  analyticsNumber: { fontSize: 28, fontWeight: '700', color: '#795548', marginBottom: 4 },
  analyticsLabel: { fontSize: 14, color: '#64748B' },
  clientsStats: { flexDirection: 'row', gap: 12, marginBottom: 24 },
  clientsStatCard: { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  clientsStatNumber: { fontSize: 28, fontWeight: '700', color: '#3fb5afff', marginBottom: 4 },
  clientsStatLabel: { fontSize: 14, color: '#64748B' },
  fab: { position: 'absolute', bottom: 20, right: 20, width: 56, height: 56, borderRadius: 28, backgroundColor: '#4A90E2', alignItems: 'center', justifyContent: 'center', shadowColor: '#4A90E2', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 8 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContainer: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: height * 0.8 },
  fullModalContainer: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, height: height * 0.9 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  modalContent: { padding: 20 },
  searchModalContainer: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, height: height * 0.8 },
  searchHeader: { flexDirection: 'row', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  searchInput: { flex: 1, fontSize: 16, color: '#0F172A', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#F8FAFC', borderRadius: 8, marginRight: 12 },
  searchResults: { flex: 1, padding: 20 },
  searchResultItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  lockedSearchResult: { opacity: 0.6 },
  searchResultContent: { flex: 1, marginLeft: 12 },
  searchResultTitle: { fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  lockedSearchResultTitle: { color: '#94A3B8' },
  searchResultCategory: { fontSize: 12, color: '#64748B' },
  notificationItem: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  notificationIcon: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  notificationContent: { flex: 1 },
  notificationHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  notificationTitle: { fontSize: 14, fontWeight: '600', color: '#0F172A', flex: 1 },
  priorityBadge: { borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 },
  priorityText: { fontSize: 8, fontWeight: '600', color: '#fff' },
  notificationMessage: { fontSize: 13, color: '#64748B', marginBottom: 4 },
  notificationTime: { fontSize: 11, color: '#94A3B8' },
  quickActionsModalGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  quickActionModalCard: { alignItems: 'center', width: (width - 80) / 3 },
  lockedQuickActionModalCard: { opacity: 0.6 },
  quickActionModalIcon: { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginBottom: 8, position: 'relative' },
  quickActionLockOverlay: { position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 8, backgroundColor: '#ef4444', justifyContent: 'center', alignItems: 'center' },
  quickActionModalText: { fontSize: 12, fontWeight: '500', color: '#0F172A', textAlign: 'center' },
  lockedQuickActionModalText: { color: '#94A3B8' },
  teamStats: { backgroundColor: '#F8FAFC', borderRadius: 8, padding: 12, marginBottom: 16 },
  teamStatsText: { fontSize: 14, color: '#64748B', textAlign: 'center' },
  teamMemberItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  teamMemberAvatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#4A90E2', alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  teamMemberInitials: { fontSize: 14, fontWeight: '600', color: '#fff' },
  teamMemberInfo: { flex: 1 },
  teamMemberName: { fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  teamMemberRole: { fontSize: 12, color: '#64748B', marginBottom: 2 },
  teamMemberEmail: { fontSize: 11, color: '#94A3B8' },
  teamMemberStatus: { width: 8, height: 8, borderRadius: 4 },
  emptyTeam: { alignItems: 'center', paddingVertical: 32 },
  emptyTeamText: { fontSize: 16, color: '#64748B', marginTop: 12, marginBottom: 16 },
  addTeamButton: { backgroundColor: '#4A90E2', borderRadius: 8, paddingVertical: 12, paddingHorizontal: 24 },
  addTeamButtonText: { fontSize: 14, fontWeight: '600', color: '#fff' },
  solutionsOverview: { flexDirection: 'row', justifyContent: 'space-around', backgroundColor: '#F8FAFC', paddingVertical: 16, marginHorizontal: 20, borderRadius: 8, marginBottom: 16 },
  solutionsCount: { fontSize: 20, fontWeight: '700', color: '#4A90E2', textAlign: 'center' },
  solutionsCategories: { fontSize: 20, fontWeight: '700', color: '#E91E63', textAlign: 'center' },
  solutionsList: { flex: 1 },
  solutionModalItem: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingVertical: 12, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  lockedSolutionModalItem: { opacity: 0.6 },
  solutionModalIcon: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', marginRight: 12, position: 'relative' },
  solutionModalLock: { position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.1)', justifyContent: 'center', alignItems: 'center' },
  solutionModalContent: { flex: 1 },
  solutionModalTitle: { fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  lockedSolutionModalTitle: { color: '#94A3B8' },
  solutionModalDescription: { fontSize: 12, color: '#64748B' },
  solutionPlanBadge: { backgroundColor: '#fef3c7', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2, marginRight: 8 },
  solutionPlanBadgeText: { fontSize: 8, fontWeight: '600', color: '#92400e' },
  solutionSectionHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F8FAFC', paddingVertical: 16, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  solutionSectionIcon: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  solutionSectionContent: { flex: 1 },
  solutionSectionTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  solutionSectionCount: { fontSize: 12, color: '#64748B' },
  sectionPlanBadge: { backgroundColor: '#fef3c7', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4 },
  sectionPlanBadgeText: { fontSize: 10, fontWeight: '600', color: '#92400e' },
  emptyNotifications: { alignItems: 'center', paddingVertical: 32 },
  emptyNotificationsText: { fontSize: 16, color: '#64748B', marginTop: 12 },
  syncIndicator: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8, backgroundColor: 'rgba(255,255,255,0.1)', padding: 8, borderRadius: 8 },
  syncText: { color: '#fff', marginLeft: 8, fontSize: 12, fontWeight: '500' },
  offlineIndicator: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8, backgroundColor: 'rgba(239,68,68,0.2)', padding: 8, borderRadius: 8 },
  offlineText: { color: '#fff', marginLeft: 8, fontSize: 12, fontWeight: '500' },

  // Market Intelligence Card
  marketCard: { backgroundColor: '#fff', borderRadius: 14, marginBottom: 16, borderWidth: 1, borderColor: '#e2e8f0', overflow: 'hidden', elevation: 2 },
  marketCardHeader: { flexDirection: 'row', alignItems: 'center', padding: 14, gap: 10 },
  marketCardLeft: { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  marketCardIcon: { width: 36, height: 36, borderRadius: 10, backgroundColor: '#eff6ff', justifyContent: 'center', alignItems: 'center' },
  marketCardTitle: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  marketCardSub: { fontSize: 11, color: '#64748b', marginTop: 1 },
  marketAlertDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#ef4444', marginRight: 4 },
  marketCardBody: { paddingHorizontal: 14, paddingBottom: 14, borderTopWidth: 1, borderTopColor: '#f1f5f9' },
  marketKpiRow: { flexDirection: 'row', gap: 8, marginTop: 12, marginBottom: 12 },
  marketKpi: { flex: 1, borderRadius: 10, padding: 8, alignItems: 'center' },
  marketKpiVal: { fontSize: 16, fontWeight: '800' },
  marketKpiLabel: { fontSize: 9, color: '#64748b', textAlign: 'center', marginTop: 2 },
  marketChallRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  marketChallIcon: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  marketChallTitle: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  marketChallDesc: { fontSize: 11, color: '#64748b', lineHeight: 15, marginTop: 1 },
  marketViewAllBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#eff6ff', borderRadius: 10, padding: 10, marginTop: 8, justifyContent: 'center' },
  marketViewAllText: { fontSize: 13, color: '#1e7be2', fontWeight: '600' },

  // Insights Tab
  insightsBanner: { flexDirection: 'row', alignItems: 'center', borderRadius: 14, padding: 14, marginBottom: 14 },
  insightsBannerTitle: { fontSize: 14, fontWeight: '700', color: '#fff' },
  insightsBannerSub: { fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 2 },
  insightsFullBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, gap: 4 },
  insightsFullBtnText: { fontSize: 11, color: '#0e4e96', fontWeight: '700' },
  insightsKpiRow: { flexDirection: 'row', gap: 8, marginBottom: 14 },
  insightsKpi: { flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 10, alignItems: 'center', borderTopWidth: 3, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  insightsKpiVal: { fontSize: 18, fontWeight: '800', marginBottom: 2 },
  insightsKpiLabel: { fontSize: 9, color: '#64748b', textAlign: 'center' },
  insightsChallengeCard: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#fff', borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  insightsChallengeTitle: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  insightsChallengeDesc: { fontSize: 12, color: '#64748b', lineHeight: 17 },
  severityBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 6 },
  severityText: { fontSize: 9, fontWeight: '800' },
  insightsBPCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 12, marginBottom: 8, borderLeftWidth: 4, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  insightsBPNum: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  insightsBPNumText: { fontSize: 11, fontWeight: '800', color: '#fff' },
  insightsBPTitle: { fontSize: 13, fontWeight: '700', color: '#1e293b', marginBottom: 2 },
  insightsBPDesc: { fontSize: 12, color: '#64748b', lineHeight: 16 },
  insightsResponseGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  insightsResponseCard: { width: '47%', backgroundColor: '#fff', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  insightsResponseIcon: { width: 38, height: 38, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  insightsResponseLabel: { fontSize: 12, fontWeight: '700', color: '#1e293b', marginBottom: 3 },
  insightsResponseDesc: { fontSize: 11, color: '#64748b', lineHeight: 15 },
  insightsFullReportBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 14, paddingVertical: 14, gap: 8, marginBottom: 20 },
  insightsFullReportBtnText: { fontSize: 15, color: '#fff', fontWeight: '700' },
});